import {
  ArrowLeft,
  ArrowRight,
  Camera,
  ExternalLink,
  ImagePlus,
  Mail,
  MousePointerClick,
  Pencil,
  Ruler,
  ShoppingCart,
  Upload,
  WandSparkles,
  createElement,
  type IconNode,
} from "lucide";

type Step = "workSetup" | "currentDevice" | "handGrip" | "photo" | "calibrate" | "comfortSignals" | "partial" | "report";
type Consent = { analytics: boolean; marketing: boolean };
type Landmark = { x: number; y: number; z?: number };
type ImageQuality = {
  width: number;
  height: number;
  blurScore: number;
  brightness: number;
  handDetected: boolean;
  landmarksCount: number;
};
type Calibration = {
  method: "credit_card_click" | "manual" | "none";
  pxPerMm?: number;
  confidence: "low" | "medium" | "high";
};
type Answers = {
  mainGoal?: "reduce_fatigue" | "improve_precision" | "replace_mouse" | "portable_setup" | "quiet_setup";
  currentDeviceType?: "standard_mouse" | "vertical_mouse" | "trackball" | "touchpad" | "keyboard_combo" | "not_sure";
  currentDevicePainPoints?: Array<"none" | "too_small" | "too_large" | "too_heavy" | "hard_clicks" | "wrist_angle" | "poor_surface">;
  deskSpace?: "roomy" | "moderate" | "compact" | "travel";
  forearmSupport?: "full" | "partial" | "edge_contact" | "none";
  purchaseIntent?: "today" | "this_week" | "researching" | "not_sure";
  measurementMode?: "photo_card" | "photo_estimate" | "manual" | "estimate";
  dominantHand: "right" | "left";
  gripStyle: "palm" | "claw" | "fingertip" | "relaxed";
  usage: "office" | "gaming" | "design" | "travel" | "mixed";
  discomfort: Array<"none" | "wrist_fatigue" | "forearm_tension" | "finger_fatigue" | "desk_pressure">;
  discomfortAreas: Array<"none" | "wrist" | "thumb" | "index_middle" | "palm" | "forearm">;
  dailyHours: number;
  breakFrequency: "regular" | "occasional" | "rare";
  deskSetup: "desk_monitor" | "laptop_first" | "standing_desk" | "compact_desk" | "shared_workspace";
  keyboardStyle: "standard" | "laptop" | "split_ergo" | "compact";
  currentSurface: "bare_desk" | "small_pad" | "full_desk_mat" | "wrist_rest_pad";
  pointerSpeed: "low_control" | "balanced" | "fast" | "not_sure";
  priority: "comfort" | "precision" | "speed" | "portability" | "quiet";
  skinSurface: "normal" | "dry" | "redness" | "callus" | "small_cut";
  budgetMinUsd: number;
  budgetMaxUsd: number;
  currentMouse: string;
  manualHandLengthMm?: number;
  manualPalmWidthMm?: number;
};
type AnalyzeResponse = {
  sessionId: string;
  quality: { accepted: boolean; issues: string[] };
  handMetrics: {
    handLengthMm: number;
    palmWidthMm: number;
    handSize: "small" | "medium" | "large";
    palmShape: "narrow" | "balanced" | "wide";
    measurementSource: string;
    confidence: "low" | "medium" | "high";
  };
  partialReport: {
    headline: string;
    preview: string;
    keyFindings: string[];
    primaryAction: string;
    visibleMetrics: Array<{ label: string; value: string }>;
    unlockPrompt: string;
  };
};
type FullReport = {
  reportVersion: "assessment_v2";
  executiveSummary: {
    profileName: string;
    verdict: string;
    confidenceLabel: string;
    primaryAction: string;
    summaryBullets: string[];
  };
  evidenceReview: Array<{ evidence: string; meaning: string; recommendation: string }>;
  comfortAssessment: {
    label: string;
    summary: string;
    dimensions: Array<{
      label: string;
      status: "clear" | "watch" | "priority";
      score: number;
      meaning: string;
      recommendation: string;
    }>;
  };
  priorityPlan: {
    fixFirst: string[];
    optimizeNext: string[];
    avoid: string[];
  };
  recommendedSetup: {
    headline: string;
    rationale: string;
    primaryProductId?: string;
    supportProductIds: string[];
    alternativeProductIds: string[];
  };
  recoveryProtocol: {
    workdayReset: string[];
    surfaceSetup: string[];
    breakRhythm: string[];
  };
  shoppingRules: Array<{ rule: string; why: string }>;
  methodNote: string;
  advisorInsights: {
    profileName: string;
    oneLineSummary: string;
    keyFindings: string[];
    setupPriorities: string[];
    recoveryProtocol: string[];
    shoppingRules: string[];
  };
  fitProfile: { title: string; summary: string; measurementConfidence: "low" | "medium" | "high" };
  comfortScore: {
    score: number;
    label: string;
    summary: string;
    bands: {
      fitMatch: number;
      wristLoad: number;
      fingerTension: number;
      deskPressure: number;
      breakHabit: number;
      skinSurface: number;
    };
  };
  topProductTypes: Array<{ label: string; buyingCriteria: string[]; fitReason: string }>;
  ownedSkuMatches: Array<{
    productId: string;
    title: string;
    productType: string;
    description: string;
    score: number;
    priceUsd: number;
    reasons: string[];
    cautions: string[];
    imageUrl?: string;
    productUrl?: string;
    accent: string;
  }>;
  bundleRecommendations: Array<{
    title: string;
    score: number;
    productIds: string[];
    reason: string;
    focus: "wrist_support" | "finger_relief" | "desk_pressure" | "skin_care" | "balanced_setup";
  }>;
  ergonomicTips: string[];
  careRoutine: string[];
  redFlags: string[];
  avoidList: string[];
  confidence: "low" | "medium" | "high";
  disclaimer: string;
};

declare global {
  interface Window {
    AIHandFitAdvisor?: {
      init: (options?: InitOptions) => void;
    };
    Shopify?: {
      customerPrivacy?: {
        analyticsProcessingAllowed?: () => boolean;
        marketingAllowed?: () => boolean;
      };
    };
  }
}

interface InitOptions {
  target?: string | HTMLElement;
  apiBase?: string;
}

interface WidgetState {
  apiBase: string;
  sessionId?: string;
  step: Step;
  loading: boolean;
  loadingMessage?: string;
  error?: string;
  imageUrl?: string;
  imageElement?: HTMLImageElement;
  landmarks?: Landmark[];
  imageQuality?: ImageQuality;
  calibration: Calibration;
  calibrationPoints: Array<{ x: number; y: number }>;
  answers: Answers;
  analysis?: AnalyzeResponse;
  report?: FullReport;
  cart?: {
    productId: string;
    checkoutUrl: string;
  };
  email: string;
  marketingConsent: boolean;
}

const CARD_MM = 85.6;
const MEDIAPIPE_WASM = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.22-rc.20250304/wasm";
const MEDIAPIPE_JS = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.22-rc.20250304/vision_bundle.mjs";
const HAND_MODEL = "https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task";

const defaultAnswers: Answers = {
  mainGoal: "reduce_fatigue",
  currentDeviceType: "standard_mouse",
  currentDevicePainPoints: ["none"],
  deskSpace: "moderate",
  forearmSupport: "partial",
  purchaseIntent: "researching",
  measurementMode: "estimate",
  dominantHand: "right",
  gripStyle: "relaxed",
  usage: "office",
  discomfort: ["none"],
  discomfortAreas: ["none"],
  dailyHours: 6,
  breakFrequency: "occasional",
  deskSetup: "desk_monitor",
  keyboardStyle: "standard",
  currentSurface: "small_pad",
  pointerSpeed: "balanced",
  priority: "comfort",
  skinSurface: "normal",
  budgetMinUsd: 25,
  budgetMaxUsd: 90,
  currentMouse: "",
};

const styles = `
.hfa-root {
  --hfa-ink: #201f1c;
  --hfa-muted: #706c66;
  --hfa-line: #ebe9e3;
  --hfa-surface: #ffffff;
  --hfa-soft: #f5f4f0;
  --hfa-page: #fafafa;
  --hfa-accent: #ffa940;
  --hfa-accent-strong: #f08c1a;
  --hfa-teal: #ffa940;
  --hfa-teal-strong: #f08c1a;
  --hfa-coral: #ffa940;
  --hfa-blue: #bae0ff;
  --hfa-green: #8db03a;
  --hfa-shadow: 0 14px 36px rgba(32,31,28,0.07);
  color: var(--hfa-ink);
  font-family: Poppins, "Helvetica Neue", Arial, sans-serif;
  margin: 0 auto;
  max-width: min(1080px, 100%);
  overflow-x: clip;
  width: 100%;
}
.hfa-root * {
  box-sizing: border-box;
  min-width: 0;
}
.hfa-shell {
  background: var(--hfa-page);
  border: 1px solid var(--hfa-line);
  border-radius: 18px;
  display: block;
  min-height: 680px;
  overflow: hidden;
  box-shadow: var(--hfa-shadow);
  width: 100%;
}
.hfa-main {
  margin: 0 auto;
  max-width: 100%;
  padding: 34px;
  width: 100%;
}
.hfa-side {
  background: #f5f4f0;
  border-left: 1px solid var(--hfa-line);
  display: none;
  flex-direction: column;
  justify-content: space-between;
  max-width: 100%;
  padding: 28px;
  width: 100%;
}
.hfa-kicker {
  color: #b8640b;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0 0 8px;
  text-transform: uppercase;
}
.hfa-title {
  font-size: 36px;
  font-weight: 600;
  line-height: 1.15;
  margin: 0 0 12px;
  max-width: 780px;
  overflow-wrap: anywhere;
}
.hfa-copy {
  color: var(--hfa-muted);
  font-size: 15px;
  line-height: 1.55;
  margin: 0 0 22px;
  max-width: 720px;
  overflow-wrap: anywhere;
}
.hfa-progress {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  margin: 0 0 24px;
}
.hfa-progress span {
  background: #e8e4dc;
  border-radius: 999px;
  display: block;
  height: 6px;
  overflow: hidden;
}
.hfa-progress span.is-active,
.hfa-progress span.is-done {
  background: var(--hfa-teal);
}
.hfa-photo-box {
  align-items: center;
  background: #ffffff;
  border: 1px dashed #ccb28f;
  border-radius: 16px;
  display: grid;
  gap: 16px;
  justify-items: center;
  min-height: 330px;
  padding: 26px;
  text-align: center;
  width: 100%;
}
.hfa-icon {
  align-items: center;
  background: #fff2db;
  border-radius: 999px;
  color: #b8640b;
  display: inline-flex;
  height: 48px;
  justify-content: center;
  width: 48px;
}
.hfa-icon svg { height: 23px; width: 23px; }
.hfa-button-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.hfa-btn {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  font-size: 15px;
  font-weight: 760;
  gap: 8px;
  justify-content: center;
  min-height: 48px;
  padding: 11px 18px;
  text-decoration: none;
  transition: background 120ms ease, border-color 120ms ease, color 120ms ease;
  white-space: normal;
}
.hfa-btn svg { height: 18px; width: 18px; }
.hfa-btn-primary {
  background: var(--hfa-accent);
  color: var(--hfa-ink);
  box-shadow: 0 8px 18px rgba(255,169,64,0.25);
}
.hfa-btn-primary:hover { background: var(--hfa-accent-strong); }
.hfa-btn-secondary {
  background: #ffffff;
  border-color: #d8d2c8;
  color: var(--hfa-ink);
}
.hfa-btn-secondary:hover { border-color: var(--hfa-accent); }
.hfa-btn[disabled] {
  cursor: not-allowed;
  opacity: 0.55;
}
.hfa-file {
  height: 1px;
  opacity: 0;
  position: absolute;
  width: 1px;
}
.hfa-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.hfa-field {
  display: grid;
  gap: 7px;
}
.hfa-field label,
.hfa-label {
  color: var(--hfa-ink);
  font-size: 13px;
  font-weight: 760;
}
.hfa-field input,
.hfa-field select {
  background: #ffffff;
  border: 1px solid #d8d2c8;
  border-radius: 12px;
  color: var(--hfa-ink);
  font: inherit;
  min-height: 44px;
  padding: 10px 12px;
  width: 100%;
}
.hfa-choice-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.hfa-choice {
  align-items: start;
  background: #ffffff;
  border: 1px solid #d8d2c8;
  border-radius: 14px;
  cursor: pointer;
  display: grid;
  gap: 5px;
  min-height: 74px;
  padding: 12px;
}
.hfa-choice input {
  height: 1px;
  opacity: 0;
  position: absolute;
  width: 1px;
}
.hfa-choice.is-selected {
  background: #fffaf1;
  border-color: var(--hfa-accent);
  box-shadow: inset 0 0 0 1px var(--hfa-accent);
}
.hfa-choice strong { font-size: 14px; }
.hfa-choice span { color: var(--hfa-muted); font-size: 12px; line-height: 1.35; }
.hfa-canvas-wrap {
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 16px;
  max-height: min(760px, 72vh);
  overflow: auto;
}
.hfa-canvas-wrap canvas {
  display: block;
  height: auto;
  max-width: 100%;
  touch-action: manipulation;
  width: 100%;
}
.hfa-calibration-help {
  background: #fff8ed;
  border: 1px solid #edd6b3;
  border-radius: 16px;
  color: var(--hfa-ink);
  display: grid;
  gap: 8px;
  margin: 14px 0;
  padding: 14px;
}
.hfa-calibration-help strong {
  font-size: 15px;
}
.hfa-calibration-help p {
  color: var(--hfa-muted);
  font-size: 13px;
  line-height: 1.45;
  margin: 0;
}
.hfa-guidance-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 14px 0 0;
}
.hfa-guidance-card {
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 14px;
  padding: 14px;
}
.hfa-guidance-card span {
  color: #b8640b;
  display: block;
  font-size: 11px;
  font-weight: 800;
  margin-bottom: 5px;
  text-transform: uppercase;
}
.hfa-guidance-card strong {
  display: block;
  font-size: 14px;
  line-height: 1.25;
  margin-bottom: 5px;
}
.hfa-guidance-card p {
  color: var(--hfa-muted);
  font-size: 12px;
  line-height: 1.42;
  margin: 0;
}
.hfa-status-list {
  display: grid;
  gap: 10px;
  margin: 20px 0;
}
.hfa-status {
  align-items: center;
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 14px;
  display: flex;
  gap: 10px;
  padding: 12px;
}
.hfa-status b { font-size: 13px; }
.hfa-status span { color: var(--hfa-muted); font-size: 13px; }
.hfa-card {
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 16px;
  padding: 16px;
}
.hfa-question-block {
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 18px;
  margin: 0 0 16px;
  padding: 18px;
}
.hfa-question-head {
  margin-bottom: 14px;
}
.hfa-question-head h2 {
  font-size: 22px;
  font-weight: 600;
  line-height: 1.25;
  margin: 0 0 6px;
}
.hfa-question-head p:not(.hfa-kicker) {
  color: var(--hfa-muted);
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
}
.hfa-wide-cta {
  min-width: 240px;
}
.hfa-metrics {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 22px 0;
}
.hfa-metric {
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 14px;
  padding: 14px;
}
.hfa-metric span {
  color: var(--hfa-muted);
  display: block;
  font-size: 12px;
  margin-bottom: 4px;
}
.hfa-metric strong {
  display: block;
  font-size: 19px;
}
.hfa-score-panel {
  align-items: center;
  background: #f7faf8;
  border: 1px solid var(--hfa-line);
  border-radius: 8px;
  display: grid;
  gap: 20px;
  grid-template-columns: 140px minmax(0, 1fr);
  margin: 0 0 22px;
  padding: 16px;
}
.hfa-score-circle {
  align-items: center;
  aspect-ratio: 1;
  background: conic-gradient(var(--hfa-accent) calc(var(--score) * 1%), #e8e4dc 0);
  border-radius: 999px;
  display: flex;
  justify-content: center;
  padding: 10px;
}
.hfa-score-inner {
  align-items: center;
  background: #ffffff;
  border-radius: 999px;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;
}
.hfa-score-inner strong {
  font-size: 32px;
  line-height: 1;
}
.hfa-score-inner span,
.hfa-band span {
  color: var(--hfa-muted);
  font-size: 12px;
}
.hfa-band-grid {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.hfa-band {
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 6px;
  padding: 9px;
}
.hfa-band strong {
  display: block;
  font-size: 15px;
  margin-top: 2px;
}
.hfa-product-list {
  display: grid;
  gap: 14px;
}
.hfa-product {
  align-items: stretch;
  border: 1px solid var(--hfa-line);
  border-radius: 8px;
  display: grid;
  gap: 14px;
  grid-template-columns: 124px minmax(0, 1fr);
  padding: 14px;
}
.hfa-product-visual {
  align-items: center;
  background: #ffffff;
  border: 1px solid #f0eee8;
  border-radius: 14px;
  display: flex;
  justify-content: center;
  min-height: 132px;
  overflow: hidden;
  position: relative;
}
.hfa-product-visual img {
  display: block;
  height: 100%;
  max-height: 160px;
  object-fit: contain;
  padding: 8px;
  width: 100%;
}
.hfa-product-visual.has-image::before,
.hfa-product-visual.has-image::after {
  content: none;
}
.hfa-product-visual::before {
  background: #1f2937;
  border-radius: 56% 44% 50% 50%;
  content: "";
  height: 84px;
  transform: rotate(-8deg);
  width: 58px;
}
.hfa-product-visual::after {
  background: rgba(255,255,255,0.72);
  border-radius: 999px;
  content: "";
  height: 28px;
  position: absolute;
  top: 34px;
  width: 3px;
}
.hfa-product-visual[data-accent="coral"],
.hfa-product-visual[data-accent="blue"],
.hfa-product-visual[data-accent="green"],
.hfa-product-visual[data-accent="gray"],
.hfa-product-visual[data-accent="teal"] { background: #ffffff; }
.hfa-product h3 {
  font-size: 19px;
  line-height: 1.2;
  margin: 0 0 6px;
}
.hfa-product p {
  color: var(--hfa-muted);
  line-height: 1.45;
  margin: 0 0 10px;
}
.hfa-product-meta {
  color: #374151;
  display: flex;
  flex-wrap: wrap;
  font-size: 13px;
  gap: 8px;
  margin-bottom: 10px;
}
.hfa-product-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.hfa-pill {
  background: #f5f4f0;
  border-radius: 999px;
  padding: 5px 8px;
}
.hfa-bundle-list {
  display: grid;
  gap: 10px;
  margin: 0 0 18px;
}
.hfa-bundle {
  background: #f7faf8;
  border: 1px solid var(--hfa-line);
  border-radius: 8px;
  padding: 14px;
}
.hfa-bundle h3 {
  font-size: 17px;
  margin: 0 0 6px;
}
.hfa-bundle p {
  color: var(--hfa-muted);
  margin: 0;
}
.hfa-advice-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 18px 0;
}
.hfa-advice-card {
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 8px;
  padding: 14px;
}
.hfa-advice-card h3 {
  font-size: 16px;
  line-height: 1.25;
  margin: 0 0 8px;
}
.hfa-advice-card p {
  color: var(--hfa-muted);
  font-size: 13px;
  line-height: 1.45;
  margin: 0;
}
.hfa-lists {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 18px;
}
.hfa-lists ul,
.hfa-product ul,
.hfa-advice-card ul {
  color: #4b5563;
  line-height: 1.5;
  margin: 8px 0 0;
  padding-left: 18px;
}
.hfa-report-hero {
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 18px;
  margin-bottom: 18px;
  padding: 22px;
}
.hfa-assessment-top {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1fr) 260px;
}
.hfa-verdict-panel {
  background: #fff8ed;
  border: 1px solid #edd6b3;
  border-radius: 16px;
  padding: 16px;
}
.hfa-verdict-panel span {
  color: #9a5b0a;
  display: block;
  font-size: 11px;
  font-weight: 800;
  margin-bottom: 6px;
  text-transform: uppercase;
}
.hfa-verdict-panel strong {
  display: block;
  font-size: 20px;
  line-height: 1.25;
}
.hfa-evidence-table {
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 16px;
  overflow: hidden;
}
.hfa-evidence-row {
  display: grid;
  gap: 14px;
  grid-template-columns: 0.92fr 1fr 1fr;
  padding: 16px;
}
.hfa-evidence-row + .hfa-evidence-row {
  border-top: 1px solid var(--hfa-line);
}
.hfa-evidence-row span {
  color: #9a5b0a;
  display: block;
  font-size: 11px;
  font-weight: 800;
  margin-bottom: 5px;
  text-transform: uppercase;
}
.hfa-evidence-row p {
  color: var(--hfa-muted);
  font-size: 13px;
  line-height: 1.48;
  margin: 0;
}
.hfa-priority-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.hfa-priority-card {
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 16px;
  padding: 16px;
}
.hfa-priority-card h3 {
  font-size: 17px;
  margin: 0 0 10px;
}
.hfa-dimension-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.hfa-dimension {
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 16px;
  padding: 16px;
}
.hfa-dimension-head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}
.hfa-dimension-head span {
  font-weight: 780;
}
.hfa-status-chip {
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  padding: 5px 8px;
  text-transform: uppercase;
}
.hfa-status-chip.is-clear { background: #eef7e4; color: #4c650f; }
.hfa-status-chip.is-watch { background: #fff2db; color: #7a4b08; }
.hfa-status-chip.is-priority { background: #fff0ed; color: #9a3412; }
.hfa-dimension p {
  color: var(--hfa-muted);
  font-size: 13px;
  line-height: 1.45;
  margin: 8px 0 0;
}
.hfa-product-intro {
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 16px;
  margin-bottom: 14px;
  padding: 16px;
}
.hfa-product-intro strong {
  display: block;
  font-size: 18px;
  margin-bottom: 6px;
}
.hfa-product-intro p {
  color: var(--hfa-muted);
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
}
.hfa-alt-details {
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 16px;
  padding: 14px 16px;
}
.hfa-alt-details summary {
  cursor: pointer;
  font-weight: 800;
}
.hfa-profile-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.hfa-context-strip,
.hfa-plan-steps {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 16px;
}
.hfa-plan-steps {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 0 0 14px;
}
.hfa-context-strip div,
.hfa-plan-steps article {
  background: rgba(255,255,255,0.78);
  border: 1px solid var(--hfa-line);
  border-radius: 14px;
  padding: 12px;
}
.hfa-context-strip span,
.hfa-plan-steps span {
  color: var(--hfa-muted);
  display: block;
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 4px;
  text-transform: uppercase;
}
.hfa-context-strip strong,
.hfa-plan-steps strong {
  display: block;
  font-size: 14px;
}
.hfa-plan-steps p {
  color: var(--hfa-muted);
  font-size: 12px;
  line-height: 1.4;
  margin: 6px 0 0;
}
.hfa-profile-tags span,
.hfa-score-badge,
.hfa-match-label {
  background: #fff2db;
  border-radius: 999px;
  color: var(--hfa-ink);
  font-size: 12px;
  font-weight: 780;
  padding: 6px 9px;
}
.hfa-report-section {
  border-top: 1px solid var(--hfa-line);
  padding: 22px 0;
}
.hfa-section-head {
  align-items: start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 12px;
}
.hfa-section-head h2 {
  font-size: 22px;
  line-height: 1.2;
  margin: 0;
}
.hfa-tight-copy {
  margin-bottom: 14px;
}
.hfa-insight-grid,
.hfa-finding-grid,
.hfa-protocol-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.hfa-protocol-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.hfa-insight,
.hfa-finding,
.hfa-protocol-card,
.hfa-report-footer article {
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 16px;
  padding: 16px;
}
.hfa-insight span {
  color: #b8640b;
  display: block;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 8px;
}
.hfa-insight p,
.hfa-finding p,
.hfa-protocol-card p,
.hfa-report-footer p {
  color: var(--hfa-muted);
  font-size: 13px;
  line-height: 1.48;
  margin: 0;
}
.hfa-finding-top {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}
.hfa-finding-top span {
  font-size: 13px;
  font-weight: 760;
}
.hfa-finding-top strong {
  color: #b8640b;
  font-size: 12px;
}
.hfa-bar {
  background: #e8e4dc;
  border-radius: 999px;
  height: 7px;
  margin-bottom: 10px;
  overflow: hidden;
}
.hfa-bar span {
  background: var(--hfa-accent);
  border-radius: inherit;
  display: block;
  height: 100%;
  width: calc(var(--value) * 1%);
}
.hfa-setup-list {
  display: grid;
  gap: 12px;
}
.hfa-setup-card {
  align-items: start;
  border: 1px solid var(--hfa-line);
  border-radius: 16px;
  display: grid;
  gap: 14px;
  grid-template-columns: 132px minmax(0, 1fr);
  padding: 14px;
}
.hfa-setup-card.is-primary {
  background: #ffffff;
  border-color: #e0b66f;
  box-shadow: 0 10px 24px rgba(32,31,28,0.06);
}
.hfa-setup-card .hfa-product-visual {
  aspect-ratio: 1;
  min-height: 0;
  width: 132px;
}
.hfa-setup-card .hfa-product-visual img {
  max-height: none;
  object-fit: contain;
  padding: 10px;
}
.hfa-setup-body h3 {
  font-size: 20px;
  line-height: 1.22;
  margin: 0 0 8px;
}
.hfa-setup-body > p {
  color: var(--hfa-muted);
  font-size: 14px;
  line-height: 1.45;
  margin: 0 0 12px;
}
.hfa-product-fields {
  display: grid;
  gap: 8px;
  margin: 0 0 14px;
}
.hfa-product-fields div {
  border-top: 1px solid #edf2ef;
  display: grid;
  gap: 8px;
  grid-template-columns: 86px minmax(0, 1fr);
  padding-top: 8px;
}
.hfa-product-fields dt {
  color: #25323a;
  font-size: 12px;
  font-weight: 800;
}
.hfa-product-fields dd {
  color: var(--hfa-muted);
  font-size: 13px;
  line-height: 1.42;
  margin: 0;
}
.hfa-match-label.is-strong { background: #fff2db; color: #6a3d00; }
.hfa-match-label.is-support { background: #eef7ff; color: #244d7b; }
.hfa-match-label.is-caution { background: #fff2d9; color: #75520e; }
.hfa-match-label.is-low { background: #f5f4f0; color: #706c66; }
.hfa-bundle-strip {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 12px;
}
.hfa-bundle-strip article {
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 14px;
  padding: 12px;
}
.hfa-bundle-strip span {
  color: #b8640b;
  display: block;
  font-size: 11px;
  font-weight: 800;
  margin-bottom: 5px;
  text-transform: uppercase;
}
.hfa-bundle-strip strong {
  display: block;
  font-size: 14px;
  margin-bottom: 5px;
}
.hfa-bundle-strip p {
  color: var(--hfa-muted);
  font-size: 12px;
  line-height: 1.4;
  margin: 0;
}
.hfa-protocol-card h3,
.hfa-report-footer h3 {
  font-size: 16px;
  line-height: 1.25;
  margin: 0 0 10px;
}
.hfa-protocol-card ul,
.hfa-report-footer ul {
  color: var(--hfa-muted);
  font-size: 13px;
  line-height: 1.45;
  margin: 0;
  padding-left: 18px;
}
.hfa-report-footer {
  border-top: 1px solid var(--hfa-line);
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding-top: 22px;
}
.hfa-side-metrics {
  display: grid;
  gap: 10px;
  margin: 14px 0;
}
.hfa-side-metrics div,
.hfa-side-note {
  background: #ffffff;
  border: 1px solid #e2d9c8;
  border-radius: 14px;
  padding: 12px;
}
.hfa-side-metrics span {
  color: var(--hfa-muted);
  display: block;
  font-size: 12px;
  margin-bottom: 4px;
}
.hfa-side-metrics strong,
.hfa-side-note strong {
  display: block;
  font-size: 18px;
}
.hfa-side-note {
  margin-top: 14px;
}
.hfa-side h2 {
  font-size: 22px;
  margin: 0 0 12px;
}
.hfa-side p,
.hfa-fineprint {
  color: var(--hfa-muted);
  font-size: 13px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.hfa-error {
  background: #fff2ef;
  border: 1px solid #efb4a4;
  border-radius: 14px;
  color: #9a3412;
  margin: 14px 0;
  padding: 12px;
}
.hfa-success {
  background: #f2f7e6;
  border: 1px solid #c7d99a;
  border-radius: 16px;
  color: #425817;
  margin: 14px 0;
  padding: 14px;
}
.hfa-success p {
  color: #59623f;
  margin: 6px 0 12px;
}
.hfa-loading {
  align-items: center;
  background: #ffffff;
  border: 1px solid var(--hfa-line);
  border-radius: 14px;
  color: var(--hfa-ink);
  display: flex;
  gap: 10px;
  margin: 14px 0;
  padding: 12px;
}
.hfa-loading::before {
  animation: hfa-spin 900ms linear infinite;
  border: 2px solid #e8e4dc;
  border-top-color: var(--hfa-accent);
  border-radius: 999px;
  content: "";
  height: 18px;
  width: 18px;
}
@keyframes hfa-spin { to { transform: rotate(360deg); } }
.hfa-hidden { display: none !important; }
@media (max-width: 860px) {
  .hfa-root { max-width: calc(100vw - 24px); }
  .hfa-shell { display: block; min-height: 0; }
  .hfa-side { border-left: 0; border-top: 1px solid var(--hfa-line); }
  .hfa-title { font-size: 28px; max-width: 100%; }
  .hfa-copy { max-width: 100%; }
  .hfa-grid,
  .hfa-choice-grid,
  .hfa-lists,
  .hfa-metrics,
  .hfa-score-panel,
  .hfa-band-grid,
  .hfa-advice-grid,
  .hfa-insight-grid,
  .hfa-finding-grid,
  .hfa-assessment-top,
  .hfa-evidence-row,
  .hfa-priority-grid,
  .hfa-dimension-grid,
  .hfa-guidance-grid,
  .hfa-protocol-grid,
  .hfa-context-strip,
  .hfa-plan-steps,
  .hfa-bundle-strip,
  .hfa-report-footer { grid-template-columns: 1fr; }
  .hfa-product,
  .hfa-setup-card { grid-template-columns: 1fr; }
  .hfa-setup-card .hfa-product-visual { width: 100%; }
  .hfa-product-fields div { grid-template-columns: 1fr; }
  .hfa-main,
  .hfa-side { padding: 20px; }
}
`;

type HandLandmarkerRuntime = {
  detect: (source: HTMLImageElement | HTMLCanvasElement) => { landmarks?: Landmark[][] };
};

let handLandmarkerPromise: Promise<HandLandmarkerRuntime | undefined> | undefined;

const iconNodes = {
  ArrowLeft,
  ArrowRight,
  Camera,
  ExternalLink,
  ImagePlus,
  Mail,
  MousePointerClick,
  Pencil,
  Ruler,
  ShoppingCart,
  Upload,
  WandSparkles,
} satisfies Record<string, IconNode>;

type IconName = keyof typeof iconNodes;

function getIcon(name: IconName): SVGElement {
  return createElement(iconNodes[name], {
    width: 20,
    height: 20,
    "stroke-width": 2,
    "aria-hidden": "true",
  }) as SVGElement;
}

function iconHtml(name: IconName): string {
  const span = document.createElement("span");
  span.appendChild(getIcon(name));
  return span.innerHTML;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "\"": "&quot;",
    "'": "&#039;",
  }[char] ?? char));
}

function displayText(value: string): string {
  return value
    .replace(/wrist strain/gi, "wrist-load support")
    .replace(/\bstrain\b/gi, "load");
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function injectStyles(): void {
  if (document.querySelector("style[data-hfa-style]")) return;
  const style = document.createElement("style");
  style.dataset.hfaStyle = "true";
  style.textContent = styles;
  document.head.appendChild(style);
}

function getConsent(marketingRequested = false): Consent {
  const privacy = window.Shopify?.customerPrivacy;
  try {
    const analytics = typeof privacy?.analyticsProcessingAllowed === "function"
      ? Boolean(privacy.analyticsProcessingAllowed())
      : true;
    const marketing = marketingRequested && (typeof privacy?.marketingAllowed === "function"
      ? Boolean(privacy.marketingAllowed())
      : true);
    return { analytics, marketing };
  } catch {
    return { analytics: false, marketing: false };
  }
}

function apiBaseFromTarget(target: HTMLElement, options?: InitOptions): string {
  const explicit = options?.apiBase || target.dataset.apiBase;
  if (explicit && explicit.trim()) return explicit.trim().replace(/\/$/, "");
  return window.location.origin;
}

async function apiFetch<T>(state: WidgetState, path: string, body: unknown): Promise<T> {
  const response = await fetch(`${state.apiBase}${path}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || `Request failed with ${response.status}`);
  return json as T;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function fetchReportIfReady(state: WidgetState): Promise<FullReport | undefined> {
  const response = await fetch(`${state.apiBase}/api/fit/report`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ sessionId: state.sessionId }),
  });
  const json = await response.json();
  if (response.status === 202) return undefined;
  if (!response.ok) throw new Error(json.error || `Request failed with ${response.status}`);
  return json as FullReport;
}

async function waitForReport(state: WidgetState, onWaiting: () => void): Promise<FullReport> {
  for (let attempt = 0; attempt < 14; attempt += 1) {
    const report = await fetchReportIfReady(state);
    if (report) return report;
    onWaiting();
    await delay(attempt < 4 ? 900 : 1500);
  }
  throw new Error("The AI report is taking longer than expected. Please try unlocking it again in a moment.");
}

async function track(state: WidgetState, eventName: string, properties: Record<string, unknown> = {}, marketing = false): Promise<void> {
  const consent = getConsent(marketing);
  if (!consent.analytics && !consent.marketing) return;
  try {
    await apiFetch(state, "/api/fit/event", {
      sessionId: state.sessionId,
      eventName,
      properties,
      consent,
    });
  } catch {
    // Analytics should never interrupt the fit flow.
  }
}

async function createSession(state: WidgetState): Promise<void> {
  if (state.sessionId) return;
  const result = await apiFetch<{ sessionId: string }>(state, "/api/fit/session", {});
  state.sessionId = result.sessionId;
  await track(state, "quiz_started");
}

async function getHandLandmarker(): Promise<HandLandmarkerRuntime | undefined> {
  handLandmarkerPromise ??= (async () => {
    try {
      const { FilesetResolver, HandLandmarker } = await import(MEDIAPIPE_JS);
      const vision = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM);
      return HandLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: HAND_MODEL,
          delegate: "GPU",
        },
        runningMode: "IMAGE",
        numHands: 1,
      });
    } catch {
      return undefined;
    }
  })();
  return handLandmarkerPromise;
}

function imageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("The image could not be loaded."));
    image.src = url;
  });
}

function imageSourceForDetection(image: HTMLImageElement): HTMLImageElement | HTMLCanvasElement {
  const maxEdge = 1280;
  const longest = Math.max(image.naturalWidth, image.naturalHeight);
  if (longest <= maxEdge) return image;
  const scale = maxEdge / longest;
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(image.naturalWidth * scale);
  canvas.height = Math.round(image.naturalHeight * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) return image;
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function computeQuality(image: HTMLImageElement, handDetected: boolean, landmarksCount: number): ImageQuality {
  const canvas = document.createElement("canvas");
  const size = 180;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    return {
      width: image.naturalWidth,
      height: image.naturalHeight,
      blurScore: 0.5,
      brightness: 0.5,
      handDetected,
      landmarksCount,
    };
  }
  ctx.drawImage(image, 0, 0, size, size);
  const pixels = ctx.getImageData(0, 0, size, size).data;
  let brightness = 0;
  let edge = 0;
  let count = 0;
  for (let i = 0; i < pixels.length; i += 4) {
    const gray = pixels[i] * 0.299 + pixels[i + 1] * 0.587 + pixels[i + 2] * 0.114;
    brightness += gray / 255;
    const next = i + 4 < pixels.length ? pixels[i + 4] * 0.299 + pixels[i + 5] * 0.587 + pixels[i + 6] * 0.114 : gray;
    edge += Math.abs(gray - next) / 255;
    count += 1;
  }
  return {
    width: image.naturalWidth,
    height: image.naturalHeight,
    blurScore: Math.max(0, Math.min(1, edge / count * 8)),
    brightness: Math.max(0, Math.min(1, brightness / count)),
    handDetected,
    landmarksCount,
  };
}

async function analyzePhoto(file: File): Promise<{ image: HTMLImageElement; landmarks?: Landmark[]; quality: ImageQuality }> {
  const image = await imageFromFile(file);
  const landmarker = await getHandLandmarker();
  let landmarks: Landmark[] | undefined;
  if (landmarker) {
    const result = landmarker.detect(imageSourceForDetection(image));
    const detected = result.landmarks?.[0];
    if (detected?.length === 21) {
      landmarks = detected.map((point) => ({
        x: clamp01(point.x),
        y: clamp01(point.y),
        z: point.z,
      }));
    }
  }
  const quality = computeQuality(image, Boolean(landmarks), landmarks?.length ?? 0);
  return { image, landmarks, quality };
}

function drawCalibrationCanvas(state: WidgetState, root: HTMLElement): void {
  const canvas = root.querySelector<HTMLCanvasElement>("[data-hfa-canvas]");
  const image = state.imageElement;
  if (!canvas || !image) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const maxWidth = Math.min(820, image.naturalWidth);
  const scale = maxWidth / image.naturalWidth;
  canvas.width = maxWidth;
  canvas.height = Math.round(image.naturalHeight * scale);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  if (state.landmarks) {
    ctx.fillStyle = "#8DB03A";
    for (const landmark of state.landmarks) {
      ctx.beginPath();
      ctx.arc(landmark.x * canvas.width, landmark.y * canvas.height, 3, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  ctx.strokeStyle = "#FFA940";
  ctx.fillStyle = "#FFA940";
  ctx.lineWidth = 3;
  state.calibrationPoints.forEach((point, index) => {
    ctx.beginPath();
    ctx.arc(point.x, point.y, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 12px sans-serif";
    ctx.fillText(String(index + 1), point.x - 4, point.y + 4);
    ctx.fillStyle = "#FFA940";
  });
  if (state.calibrationPoints.length === 2) {
    const [a, b] = state.calibrationPoints;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }
}

function stepIndex(step: Step): number {
  const order: Step[] = ["workSetup", "currentDevice", "handGrip", "photo", "calibrate", "comfortSignals", "partial", "report"];
  if (step === "photo" || step === "calibrate") return 3;
  return order.indexOf(step);
}

function progressHtml(step: Step): string {
  const current = Math.min(stepIndex(step), 5);
  return `<div class="hfa-progress" aria-label="Fit check progress">
    ${[0, 1, 2, 3, 4, 5].map((item) => `<span class="${item < current ? "is-done" : item === current ? "is-active" : ""}"></span>`).join("")}
  </div>`;
}

function shell(content: string, side: string, state: WidgetState): string {
  return `
    <div class="hfa-root">
      <div class="hfa-shell">
        <section class="hfa-main">
          ${progressHtml(state.step)}
          ${state.error ? `<div class="hfa-error">${escapeHtml(state.error)}</div>` : ""}
          ${state.loading && state.loadingMessage ? `<div class="hfa-loading">${escapeHtml(state.loadingMessage)}</div>` : ""}
          ${content}
        </section>
        <aside class="hfa-side">
          <div>${side}</div>
        </aside>
      </div>
    </div>
  `;
}

function renderPhoto(state: WidgetState): string {
  return shell(`
    <p class="hfa-kicker">AI Hand Fit Advisor</p>
    <h1 class="hfa-title">Find the mouse shape that fits your hand.</h1>
    <p class="hfa-copy">Upload one clear photo with your hand flat and a credit card next to it. The photo stays in your browser; only measurements and answers are sent for the fit report.</p>
    <div class="hfa-photo-box">
      <span class="hfa-icon">${iconHtml("Camera")}</span>
      <div>
        <h2>Start with a hand photo</h2>
        <p class="hfa-copy">Use bright light, keep the full hand in frame, and place a card beside the hand for scale.</p>
      </div>
      <div class="hfa-button-row">
        <label class="hfa-btn hfa-btn-primary">
          ${iconHtml("Upload")} Upload photo
          <input class="hfa-file" data-hfa-photo type="file" accept="image/*" />
        </label>
      </div>
    </div>
  `, `
    <p class="hfa-kicker">Privacy first</p>
    <h2>No image is sent to DeepSeek.</h2>
    <p>The browser extracts hand landmarks and the server receives measurements, quality signals, and quiz answers. The report focuses on ergonomic fit and desk comfort.</p>
  `, state);
}

function renderCalibrate(state: WidgetState): string {
  const points = state.calibrationPoints.length;
  const detected = state.imageQuality?.handDetected ? "Hand detected" : "Photo estimate only";
  const detectedCopy = state.imageQuality?.handDetected
    ? `${state.imageQuality?.landmarksCount ?? 0} hand points found`
    : "You can still continue with estimate or enter measurements";
  const scaleTitle = points === 2 ? "Scale set" : points === 1 ? "First point saved" : "Scale is optional";
  const scaleCopy = points === 2
    ? "The card edge will be used to improve size confidence"
    : points === 1
      ? "Now tap the other end of the same long card edge"
      : "If a credit card is visible, tap the two ends of its long edge";
  const primaryLabel = points === 2 ? "Use card scale and continue" : "Skip scale and continue";
  return shell(`
    <p class="hfa-kicker">Photo scale</p>
    <h1 class="hfa-title">Optional: set the scale from a card.</h1>
    <p class="hfa-copy">The image below keeps the original photo proportion. If it looks too large, scroll inside the photo box. If there is no card in the photo, skip scale and continue.</p>
    <div class="hfa-calibration-help">
      <strong>${points === 0 ? "How to calibrate: tap point 1, then point 2." : points === 1 ? "Good. Tap the other end of the same card edge." : "Scale is ready."}</strong>
      <p>Use the long edge of a credit-card-sized card. Tap directly on the photo, not on the status text. This step is optional.</p>
    </div>
    <div class="hfa-canvas-wrap"><canvas data-hfa-canvas></canvas></div>
    <div class="hfa-status-list">
      <div class="hfa-status"><b>${escapeHtml(detected)}</b><span>${escapeHtml(detectedCopy)}</span></div>
      <div class="hfa-status"><b>${escapeHtml(scaleTitle)}</b><span>${escapeHtml(scaleCopy)}</span></div>
    </div>
    <div class="hfa-button-row">
      <button class="hfa-btn hfa-btn-primary" data-hfa-calibration-next>${iconHtml("Ruler")} ${escapeHtml(primaryLabel)}</button>
      <button class="hfa-btn hfa-btn-secondary" data-hfa-reset-calibration>${iconHtml("MousePointerClick")} ${points > 0 ? "Reset points" : "I will tap card points"}</button>
      <button class="hfa-btn hfa-btn-secondary" data-hfa-skip-calibration>${iconHtml("Pencil")} Enter manually</button>
      <button class="hfa-btn hfa-btn-secondary" data-hfa-upload-again>${iconHtml("ImagePlus")} Use another photo</button>
    </div>
  `, `
    <p class="hfa-kicker">Measurement</p>
    <h2>Card scale is helpful, not required.</h2>
    <p>The long edge uses ${CARD_MM} mm as the scale. A clear card improves sizing, while manual numbers or the built-in estimate keep the flow moving.</p>
  `, state);
}

function selectedClass(value: string, selected: string | string[]): string {
  return Array.isArray(selected) ? selected.includes(value) ? "is-selected" : "" : value === selected ? "is-selected" : "";
}

function choiceCard(name: string, value: string, selected: string | string[], label: string, copy: string, type: "radio" | "checkbox" = "radio"): string {
  const checked = Array.isArray(selected) ? selected.includes(value) : selected === value;
  const role = type === "radio" ? "radio" : "checkbox";
  return `
    <div class="hfa-choice ${selectedClass(value, selected)}" data-hfa-choice role="${role}" tabindex="0" aria-checked="${checked ? "true" : "false"}">
      <input type="${type}" name="${name}" value="${value}" ${checked ? "checked" : ""} />
      <strong>${escapeHtml(label)}</strong><span>${escapeHtml(copy)}</span>
    </div>
  `;
}

function questionBlock(kicker: string, title: string, copy: string, body: string): string {
  return `
    <section class="hfa-question-block">
      <div class="hfa-question-head">
        <p class="hfa-kicker">${escapeHtml(kicker)}</p>
        <h2>${escapeHtml(title)}</h2>
        <p>${escapeHtml(copy)}</p>
      </div>
      ${body}
    </section>
  `;
}

function renderWorkSetup(state: WidgetState): string {
  return shell(`
    <p class="hfa-kicker">AI Hand Advisor</p>
    <h1 class="hfa-title">Start with your real desk setup.</h1>
    <p class="hfa-copy">A hand photo helps, but the best recommendation also depends on desk space, keyboard position, surface friction, and how your forearm is supported.</p>
    <form data-hfa-step-form data-hfa-next-step="currentDevice">
      ${questionBlock("Step 1", "Work setup", "This sets the context before any product recommendation appears.", `
        <p class="hfa-label">Main goal</p>
        <div class="hfa-choice-grid">
          ${[
            ["reduce_fatigue", "Reduce fatigue", "Lower long-session hand and wrist load"],
            ["improve_precision", "Improve precision", "Better control for detailed work"],
            ["replace_mouse", "Replace mouse", "Find a better everyday shape"],
            ["portable_setup", "Portable setup", "Travel or shared desks"],
            ["quiet_setup", "Quiet setup", "Reduce desk noise and distraction"],
          ].map(([value, label, copy]) => choiceCard("mainGoal", value, state.answers.mainGoal ?? "reduce_fatigue", label, copy)).join("")}
        </div>
        <div style="height:16px"></div>
        <div class="hfa-grid">
          <div class="hfa-field">
            <label for="hfa-hours">Daily mouse/keyboard hours</label>
            <input id="hfa-hours" name="dailyHours" type="number" min="1" max="16" value="${state.answers.dailyHours}" />
          </div>
          <div class="hfa-field">
            <label for="hfa-breaks">Break rhythm</label>
            <select id="hfa-breaks" name="breakFrequency">
              <option value="regular" ${state.answers.breakFrequency === "regular" ? "selected" : ""}>Regular breaks</option>
              <option value="occasional" ${state.answers.breakFrequency === "occasional" ? "selected" : ""}>Occasional breaks</option>
              <option value="rare" ${state.answers.breakFrequency === "rare" ? "selected" : ""}>Rare breaks</option>
            </select>
          </div>
        </div>
      `)}
      ${questionBlock("Desk geometry", "Space, keyboard, and support", "These answers separate product fit from workstation pressure.", `
        <p class="hfa-label">Desk setup</p>
        <div class="hfa-choice-grid">
          ${[
            ["desk_monitor", "Monitor desk", "External monitor, fixed desk"],
            ["laptop_first", "Laptop-first", "Laptop keyboard or small riser"],
            ["standing_desk", "Standing desk", "Height changes during the day"],
            ["compact_desk", "Compact desk", "Limited mouse space"],
            ["shared_workspace", "Shared workspace", "Different desk or travel setups"],
          ].map(([value, label, copy]) => choiceCard("deskSetup", value, state.answers.deskSetup, label, copy)).join("")}
        </div>
        <div style="height:14px"></div>
        <p class="hfa-label">Desk space</p>
        <div class="hfa-choice-grid">
          ${[
            ["roomy", "Roomy", "Pointer can move freely"],
            ["moderate", "Moderate", "Normal desk space"],
            ["compact", "Compact", "Mouse area is tight"],
            ["travel", "Travel / shared", "Setup changes often"],
          ].map(([value, label, copy]) => choiceCard("deskSpace", value, state.answers.deskSpace ?? "moderate", label, copy)).join("")}
        </div>
        <div style="height:14px"></div>
        <p class="hfa-label">Forearm support</p>
        <div class="hfa-choice-grid">
          ${[
            ["full", "Full support", "Forearm rests comfortably"],
            ["partial", "Partial support", "Some support, some reach"],
            ["edge_contact", "Desk-edge contact", "Wrist or palm hits the edge"],
            ["none", "Little support", "Arm floats or reaches"],
          ].map(([value, label, copy]) => choiceCard("forearmSupport", value, state.answers.forearmSupport ?? "partial", label, copy)).join("")}
        </div>
      `)}
      <div class="hfa-button-row">
        <button class="hfa-btn hfa-btn-primary hfa-wide-cta" type="submit">${iconHtml("ArrowRight")} Continue</button>
      </div>
    </form>
  `, "", state);
}

function renderCurrentDevice(state: WidgetState): string {
  return shell(`
    <p class="hfa-kicker">Current Device</p>
    <h1 class="hfa-title">What are you using now?</h1>
    <p class="hfa-copy">This helps the advisor explain the tradeoff, not just name a product type.</p>
    <form data-hfa-step-form data-hfa-next-step="handGrip">
      ${questionBlock("Step 2", "Current device feedback", "Pick the closest current setup and the repeated problems you notice.", `
        <p class="hfa-label">Current pointer or combo</p>
        <div class="hfa-choice-grid">
          ${[
            ["standard_mouse", "Standard mouse", "Regular office mouse"],
            ["vertical_mouse", "Vertical mouse", "Already using a tilted shape"],
            ["trackball", "Trackball", "Pointer moves with a ball"],
            ["touchpad", "Touchpad", "Laptop or external touchpad"],
            ["keyboard_combo", "Keyboard combo", "Keyboard and mouse setup"],
            ["not_sure", "Not sure", "Keep this neutral"],
          ].map(([value, label, copy]) => choiceCard("currentDeviceType", value, state.answers.currentDeviceType ?? "standard_mouse", label, copy)).join("")}
        </div>
        <div style="height:14px"></div>
        <p class="hfa-label">Current-device pain points</p>
        <div class="hfa-choice-grid">
          ${[
            ["none", "No clear issue", "Mainly comparing fit"],
            ["too_small", "Feels too small", "Palm or fingers feel cramped"],
            ["too_large", "Feels too large", "Reach or shell height feels off"],
            ["too_heavy", "Feels heavy", "Movement takes effort"],
            ["hard_clicks", "Hard clicks", "Clicking or hovering feels tiring"],
            ["wrist_angle", "Awkward wrist angle", "Wrist bends or rotates"],
            ["poor_surface", "Poor surface", "Glide or desk edge feels wrong"],
          ].map(([value, label, copy]) => choiceCard("currentDevicePainPoints", value, state.answers.currentDevicePainPoints ?? ["none"], label, copy, "checkbox")).join("")}
        </div>
      `)}
      ${questionBlock("Preference", "Control, priority, and timing", "These choices keep the report practical instead of generic.", `
        <div class="hfa-grid">
          <div class="hfa-field">
            <label for="hfa-current">Current mouse or complaint</label>
            <input id="hfa-current" name="currentMouse" type="text" maxlength="120" value="${escapeHtml(state.answers.currentMouse)}" placeholder="Optional, e.g. small travel mouse feels cramped" />
          </div>
          <div class="hfa-field">
            <label for="hfa-intent">Purchase timing</label>
            <select id="hfa-intent" name="purchaseIntent">
              <option value="today" ${state.answers.purchaseIntent === "today" ? "selected" : ""}>Ready today</option>
              <option value="this_week" ${state.answers.purchaseIntent === "this_week" ? "selected" : ""}>This week</option>
              <option value="researching" ${state.answers.purchaseIntent === "researching" ? "selected" : ""}>Researching</option>
              <option value="not_sure" ${state.answers.purchaseIntent === "not_sure" ? "selected" : ""}>Not sure</option>
            </select>
          </div>
        </div>
        <div style="height:14px"></div>
        <p class="hfa-label">Pointer speed feel</p>
        <div class="hfa-choice-grid">
          ${[
            ["low_control", "Low / controlled", "More arm movement, less sensitivity"],
            ["balanced", "Balanced", "Normal office feel"],
            ["fast", "Fast", "Small movements cover distance"],
            ["not_sure", "Not sure", "Let the advisor keep this neutral"],
          ].map(([value, label, copy]) => choiceCard("pointerSpeed", value, state.answers.pointerSpeed, label, copy)).join("")}
        </div>
        <div style="height:14px"></div>
        <p class="hfa-label">Primary product priority</p>
        <div class="hfa-choice-grid">
          ${[
            ["comfort", "Comfort", "Reduce long-session load"],
            ["precision", "Precision", "Control for design or detail work"],
            ["speed", "Speed", "Fast movement and light corrections"],
            ["portability", "Portability", "Travel or shared desk use"],
            ["quiet", "Quiet", "Low-noise work environment"],
          ].map(([value, label, copy]) => choiceCard("priority", value, state.answers.priority, label, copy)).join("")}
        </div>
      `)}
      <div class="hfa-button-row">
        <button class="hfa-btn hfa-btn-secondary" type="button" data-hfa-back="workSetup">${iconHtml("ArrowLeft")} Back</button>
        <button class="hfa-btn hfa-btn-primary hfa-wide-cta" type="submit">${iconHtml("ArrowRight")} Continue</button>
      </div>
    </form>
  `, "", state);
}

function renderHandGrip(state: WidgetState): string {
  return shell(`
    <p class="hfa-kicker">Hand & Grip</p>
    <h1 class="hfa-title">Add hand context, then choose photo or estimate.</h1>
    <p class="hfa-copy">The photo is optional. If you skip it, the report will use manual measurements if provided, or a lower-confidence estimate.</p>
    <form data-hfa-hand-form>
      ${questionBlock("Step 3", "Hand use", "These are fit signals, not medical observations.", `
        <div class="hfa-grid">
          <div class="hfa-field">
            <label for="hfa-dominant">Dominant hand</label>
            <select id="hfa-dominant" name="dominantHand">
              <option value="right" ${state.answers.dominantHand === "right" ? "selected" : ""}>Right</option>
              <option value="left" ${state.answers.dominantHand === "left" ? "selected" : ""}>Left</option>
            </select>
          </div>
          <div class="hfa-field">
            <label for="hfa-usage">Main use</label>
            <select id="hfa-usage" name="usage">
              ${["office", "gaming", "design", "travel", "mixed"].map((value) => `<option value="${value}" ${state.answers.usage === value ? "selected" : ""}>${value}</option>`).join("")}
            </select>
          </div>
        </div>
        <div style="height:14px"></div>
        <p class="hfa-label">Grip style</p>
        <div class="hfa-choice-grid">
          ${[
            ["relaxed", "Relaxed", "Loose everyday hold"],
            ["palm", "Palm", "Palm rests on the shell"],
            ["claw", "Claw", "Arched fingers, faster clicks"],
            ["fingertip", "Fingertip", "Fingers steer the mouse"],
          ].map(([value, label, copy]) => choiceCard("gripStyle", value, state.answers.gripStyle, label, copy)).join("")}
        </div>
      `)}
      <section class="hfa-question-block">
        <div class="hfa-question-head">
          <p class="hfa-kicker">Measurement</p>
          <h2>Choose measurement path</h2>
          <p>Use a hand photo for better fit confidence, or continue with an estimate and optional manual numbers.</p>
        </div>
        <div class="hfa-guidance-grid">
          <article class="hfa-guidance-card">
            <span>01</span>
            <strong>Show the full hand</strong>
            <p>Lay the hand flat, keep all fingertips and the wrist area inside the frame.</p>
          </article>
          <article class="hfa-guidance-card">
            <span>02</span>
            <strong>Shoot straight down</strong>
            <p>Use bright light and keep the camera parallel to the desk to avoid angle distortion.</p>
          </article>
          <article class="hfa-guidance-card">
            <span>03</span>
            <strong>Card is optional</strong>
            <p>Place a credit-card-sized card beside the hand for scale, or skip and use estimate.</p>
          </article>
        </div>
        <div style="height:14px"></div>
        <div class="hfa-button-row">
          <label class="hfa-btn hfa-btn-primary">
            ${iconHtml("Upload")} Upload optional hand photo
            <input class="hfa-file" data-hfa-photo type="file" accept="image/*" />
          </label>
          <button class="hfa-btn hfa-btn-secondary" type="button" data-hfa-continue-estimate>${iconHtml("Ruler")} Continue with estimate</button>
        </div>
      </section>
      <div class="hfa-button-row">
        <button class="hfa-btn hfa-btn-secondary" type="button" data-hfa-back="currentDevice">${iconHtml("ArrowLeft")} Back</button>
      </div>
    </form>
  `, "", state);
}

function renderQuestions(state: WidgetState): string {
  const needsManual = state.calibration.method === "manual" || state.calibration.method === "none";
  return shell(`
    <p class="hfa-kicker">Fit profile</p>
    <h1 class="hfa-title">Tell us how your desk actually works.</h1>
    <p class="hfa-copy">The report uses your hand size plus workstation context, grip, surface, break rhythm, and shopping priority to explain the recommendation.</p>
    <form data-hfa-questions>
      ${questionBlock("Step 1", "Workstation context", "These answers help separate a mouse-fit issue from a desk-layout issue.", `
        <div class="hfa-grid">
          <div class="hfa-field">
            <label for="hfa-dominant">Dominant hand</label>
            <select id="hfa-dominant" name="dominantHand">
              <option value="right" ${state.answers.dominantHand === "right" ? "selected" : ""}>Right</option>
              <option value="left" ${state.answers.dominantHand === "left" ? "selected" : ""}>Left</option>
            </select>
          </div>
          <div class="hfa-field">
            <label for="hfa-usage">Main use</label>
            <select id="hfa-usage" name="usage">
              ${["office", "gaming", "design", "travel", "mixed"].map((value) => `<option value="${value}" ${state.answers.usage === value ? "selected" : ""}>${value}</option>`).join("")}
            </select>
          </div>
        </div>
        <div style="height:14px"></div>
        <p class="hfa-label">Desk setup</p>
        <div class="hfa-choice-grid">
          ${[
            ["desk_monitor", "Monitor desk", "External monitor, fixed desk"],
            ["laptop_first", "Laptop-first", "Laptop keyboard or small riser"],
            ["standing_desk", "Standing desk", "Height changes during the day"],
            ["compact_desk", "Compact desk", "Limited mouse space"],
            ["shared_workspace", "Shared workspace", "Different desk or travel setups"],
          ].map(([value, label, copy]) => choiceCard("deskSetup", value, state.answers.deskSetup, label, copy)).join("")}
        </div>
        <div style="height:14px"></div>
        <p class="hfa-label">Keyboard setup</p>
        <div class="hfa-choice-grid">
          ${[
            ["standard", "Standard", "Full-size or typical office keyboard"],
            ["laptop", "Laptop keyboard", "Hands stay close to the laptop"],
            ["split_ergo", "Split ergonomic", "Shoulder-width typing position"],
            ["compact", "Compact", "Smaller keyboard, more mouse space"],
          ].map(([value, label, copy]) => choiceCard("keyboardStyle", value, state.answers.keyboardStyle, label, copy)).join("")}
        </div>
      `)}
      ${questionBlock("Step 2", "Grip and comfort signals", "Pick what you actually notice during a normal workday. These are only desk-comfort signals.", `
        <p class="hfa-label">Grip style</p>
        <div class="hfa-choice-grid">
          ${[
            ["relaxed", "Relaxed", "Loose everyday hold"],
            ["palm", "Palm", "Palm rests on the shell"],
            ["claw", "Claw", "Arched fingers, faster clicks"],
            ["fingertip", "Fingertip", "Fingers steer the mouse"],
          ].map(([value, label, copy]) => choiceCard("gripStyle", value, state.answers.gripStyle, label, copy)).join("")}
        </div>
        <div style="height:14px"></div>
        <p class="hfa-label">Comfort signals</p>
        <div class="hfa-choice-grid">
          ${[
            ["none", "No issue", "Mostly size matching"],
            ["wrist_fatigue", "Wrist fatigue", "Long-session tiredness"],
            ["forearm_tension", "Forearm tension", "Rotation or reach load"],
            ["finger_fatigue", "Finger fatigue", "Clicking or pinching tension"],
            ["desk_pressure", "Desk pressure", "Wrist edge pressure"],
          ].map(([value, label, copy]) => choiceCard("discomfort", value, state.answers.discomfort, label, copy, "checkbox")).join("")}
        </div>
        <div style="height:14px"></div>
        <p class="hfa-label">Where do you notice pressure?</p>
        <div class="hfa-choice-grid">
          ${[
            ["none", "No area", "No specific pressure point"],
            ["wrist", "Wrist", "Bending or edge pressure"],
            ["thumb", "Thumb", "Side grip tension"],
            ["index_middle", "Index / middle", "Clicking or hover tension"],
            ["palm", "Palm", "Shell or desk contact"],
            ["forearm", "Forearm", "Rotation or reach load"],
          ].map(([value, label, copy]) => choiceCard("discomfortAreas", value, state.answers.discomfortAreas, label, copy, "checkbox")).join("")}
        </div>
      `)}
      ${questionBlock("Step 3", "Surface and shopping priority", "This turns the report from a generic product list into a setup recommendation.", `
        <div class="hfa-grid">
          <div class="hfa-field">
            <label for="hfa-hours">Daily mouse/keyboard hours</label>
            <input id="hfa-hours" name="dailyHours" type="number" min="1" max="16" value="${state.answers.dailyHours}" />
          </div>
          <div class="hfa-field">
            <label for="hfa-breaks">Break rhythm</label>
            <select id="hfa-breaks" name="breakFrequency">
              <option value="regular" ${state.answers.breakFrequency === "regular" ? "selected" : ""}>Regular breaks</option>
              <option value="occasional" ${state.answers.breakFrequency === "occasional" ? "selected" : ""}>Occasional breaks</option>
              <option value="rare" ${state.answers.breakFrequency === "rare" ? "selected" : ""}>Rare breaks</option>
            </select>
          </div>
        </div>
        <div style="height:14px"></div>
        <p class="hfa-label">Current mouse surface</p>
        <div class="hfa-choice-grid">
          ${[
            ["bare_desk", "Bare desk", "Mouse directly on hard surface"],
            ["small_pad", "Small pad", "Limited glide area"],
            ["full_desk_mat", "Full desk mat", "Large low-friction surface"],
            ["wrist_rest_pad", "Wrist-rest pad", "Already using soft support"],
          ].map(([value, label, copy]) => choiceCard("currentSurface", value, state.answers.currentSurface, label, copy)).join("")}
        </div>
        <div style="height:14px"></div>
        <p class="hfa-label">Main priority</p>
        <div class="hfa-choice-grid">
          ${[
            ["comfort", "Comfort", "Reduce long-session load"],
            ["precision", "Precision", "Control for design or detail work"],
            ["speed", "Speed", "Fast movement and light corrections"],
            ["portability", "Portability", "Travel or shared desk use"],
            ["quiet", "Quiet", "Low-noise work environment"],
          ].map(([value, label, copy]) => choiceCard("priority", value, state.answers.priority, label, copy)).join("")}
        </div>
        <div style="height:14px"></div>
        <p class="hfa-label">Pointer speed feel</p>
        <div class="hfa-choice-grid">
          ${[
            ["low_control", "Low / controlled", "More arm movement, less sensitivity"],
            ["balanced", "Balanced", "Normal office feel"],
            ["fast", "Fast", "Small movements cover distance"],
            ["not_sure", "Not sure", "Let the advisor keep this neutral"],
          ].map(([value, label, copy]) => choiceCard("pointerSpeed", value, state.answers.pointerSpeed, label, copy)).join("")}
        </div>
      `)}
      <div class="hfa-question-block">
        <div class="hfa-question-head">
          <p class="hfa-kicker">Optional detail</p>
          <h2>Fine tune the report</h2>
          <p>These fields are optional, but they help the report explain tradeoffs more clearly.</p>
        </div>
        <div class="hfa-field">
          <label for="hfa-skin">Visible hand surface observation</label>
          <select id="hfa-skin" name="skinSurface">
            <option value="normal" ${state.answers.skinSurface === "normal" ? "selected" : ""}>Normal / nothing obvious</option>
            <option value="dry" ${state.answers.skinSurface === "dry" ? "selected" : ""}>Dryness</option>
            <option value="redness" ${state.answers.skinSurface === "redness" ? "selected" : ""}>Redness or irritation</option>
            <option value="callus" ${state.answers.skinSurface === "callus" ? "selected" : ""}>Callus or friction marks</option>
            <option value="small_cut" ${state.answers.skinSurface === "small_cut" ? "selected" : ""}>Small cut or broken skin</option>
          </select>
        </div>
        <p class="hfa-fineprint">This is a self-observation for ergonomic comfort guidance only.</p>
        <div style="height:16px"></div>
        <div class="hfa-grid">
          <div class="hfa-field">
            <label for="hfa-budget-min">Budget min USD</label>
            <input id="hfa-budget-min" name="budgetMinUsd" type="number" min="0" max="500" value="${state.answers.budgetMinUsd}" />
          </div>
          <div class="hfa-field">
            <label for="hfa-budget-max">Budget max USD</label>
            <input id="hfa-budget-max" name="budgetMaxUsd" type="number" min="0" max="500" value="${state.answers.budgetMaxUsd}" />
          </div>
        </div>
        <div style="height:16px"></div>
        <div class="hfa-field">
          <label for="hfa-current">Current mouse or complaint</label>
          <input id="hfa-current" name="currentMouse" type="text" maxlength="120" value="${escapeHtml(state.answers.currentMouse)}" placeholder="Optional, e.g. small travel mouse feels cramped" />
        </div>
      </div>
      <div class="${needsManual ? "" : "hfa-hidden"}">
        <div style="height:16px"></div>
        <div class="hfa-grid">
          <div class="hfa-field">
            <label for="hfa-hand-length">Hand length mm</label>
            <input id="hfa-hand-length" name="manualHandLengthMm" type="number" min="120" max="240" placeholder="e.g. 182" value="${state.answers.manualHandLengthMm ?? ""}" />
          </div>
          <div class="hfa-field">
            <label for="hfa-palm-width">Palm width mm</label>
            <input id="hfa-palm-width" name="manualPalmWidthMm" type="number" min="50" max="130" placeholder="e.g. 86" value="${state.answers.manualPalmWidthMm ?? ""}" />
          </div>
        </div>
      </div>
      <div style="height:20px"></div>
      <button class="hfa-btn hfa-btn-primary hfa-wide-cta" type="submit" ${state.loading ? "disabled" : ""}>${iconHtml("WandSparkles")} Build my fit report</button>
    </form>
  `, `
    <p class="hfa-kicker">Why we ask</p>
    <h2>Hand size is only half of fit.</h2>
    <p>A laptop-first desk, small mouse pad, or fast pointer speed can change the best recommendation even when two users have the same hand size.</p>
    <div class="hfa-side-note">
      <strong>Result quality</strong>
      <p>More context means a more specific report: device geometry, support surface, behavior reset, and buying criteria.</p>
    </div>
  `, state);
}

function renderComfortSignals(state: WidgetState): string {
  const needsManual = state.calibration.method === "manual" || state.calibration.method === "none";
  return shell(`
    <p class="hfa-kicker">Comfort Signals</p>
    <h1 class="hfa-title">Finish with the pressure and budget details.</h1>
    <p class="hfa-copy">This final step turns your desk context and hand profile into an evidence-based recommendation plan.</p>
    <form data-hfa-questions>
      ${questionBlock("Step 4", "Comfort signals", "Choose what you notice during a normal workday. These are ergonomic comfort signals only.", `
        <p class="hfa-label">What do you notice?</p>
        <div class="hfa-choice-grid">
          ${[
            ["none", "No issue", "Mostly size matching"],
            ["wrist_fatigue", "Wrist fatigue", "Long-session tiredness"],
            ["forearm_tension", "Forearm tension", "Rotation or reach load"],
            ["finger_fatigue", "Finger fatigue", "Clicking or pinching tension"],
            ["desk_pressure", "Desk pressure", "Wrist edge pressure"],
          ].map(([value, label, copy]) => choiceCard("discomfort", value, state.answers.discomfort, label, copy, "checkbox")).join("")}
        </div>
        <div style="height:14px"></div>
        <p class="hfa-label">Where do you notice pressure?</p>
        <div class="hfa-choice-grid">
          ${[
            ["none", "No area", "No specific pressure point"],
            ["wrist", "Wrist", "Bending or edge pressure"],
            ["thumb", "Thumb", "Side grip tension"],
            ["index_middle", "Index / middle", "Clicking or hover tension"],
            ["palm", "Palm", "Shell or desk contact"],
            ["forearm", "Forearm", "Rotation or reach load"],
          ].map(([value, label, copy]) => choiceCard("discomfortAreas", value, state.answers.discomfortAreas, label, copy, "checkbox")).join("")}
        </div>
      `)}
      ${questionBlock("Surface and budget", "Support surface and cart limits", "These details help prevent a generic recommendation.", `
        <p class="hfa-label">Current mouse surface</p>
        <div class="hfa-choice-grid">
          ${[
            ["bare_desk", "Bare desk", "Mouse directly on hard surface"],
            ["small_pad", "Small pad", "Limited glide area"],
            ["full_desk_mat", "Full desk mat", "Large low-friction surface"],
            ["wrist_rest_pad", "Wrist-rest pad", "Already using soft support"],
          ].map(([value, label, copy]) => choiceCard("currentSurface", value, state.answers.currentSurface, label, copy)).join("")}
        </div>
        <div style="height:16px"></div>
        <div class="hfa-grid">
          <div class="hfa-field">
            <label for="hfa-budget-min">Budget min USD</label>
            <input id="hfa-budget-min" name="budgetMinUsd" type="number" min="0" max="500" value="${state.answers.budgetMinUsd}" />
          </div>
          <div class="hfa-field">
            <label for="hfa-budget-max">Budget max USD</label>
            <input id="hfa-budget-max" name="budgetMaxUsd" type="number" min="0" max="500" value="${state.answers.budgetMaxUsd}" />
          </div>
        </div>
      `)}
      <div class="${needsManual ? "" : "hfa-hidden"}">
        ${questionBlock("Measurement fallback", "Optional manual measurements", "Manual numbers improve confidence when the photo scale is skipped.", `
          <div class="hfa-grid">
            <div class="hfa-field">
              <label for="hfa-hand-length">Hand length mm</label>
              <input id="hfa-hand-length" name="manualHandLengthMm" type="number" min="120" max="240" placeholder="e.g. 182" value="${state.answers.manualHandLengthMm ?? ""}" />
            </div>
            <div class="hfa-field">
              <label for="hfa-palm-width">Palm width mm</label>
              <input id="hfa-palm-width" name="manualPalmWidthMm" type="number" min="50" max="130" placeholder="e.g. 86" value="${state.answers.manualPalmWidthMm ?? ""}" />
            </div>
          </div>
        `)}
      </div>
      <div class="hfa-button-row">
        <button class="hfa-btn hfa-btn-secondary" type="button" data-hfa-back="handGrip">${iconHtml("ArrowLeft")} Back</button>
        <button class="hfa-btn hfa-btn-primary hfa-wide-cta" type="submit" ${state.loading ? "disabled" : ""}>${iconHtml("WandSparkles")} Build free preview</button>
      </div>
    </form>
  `, "", state);
}

function renderPartial(state: WidgetState): string {
  const report = state.analysis?.partialReport;
  return shell(`
    <p class="hfa-kicker">Free Summary</p>
    <h1 class="hfa-title">${escapeHtml(report?.headline ?? "Your fit preview is ready.")}</h1>
    <p class="hfa-copy">${escapeHtml(report?.preview ?? "")}</p>
    <div class="hfa-verdict-panel">
      <span>First priority</span>
      <strong>${escapeHtml(report?.primaryAction ?? "Start with the strongest fit match, then tune the support surface.")}</strong>
    </div>
    <div style="height:16px"></div>
    <div class="hfa-evidence-table">
      ${(report?.keyFindings ?? []).slice(0, 3).map((finding, index) => `
        <div class="hfa-evidence-row">
          <div><span>Finding ${index + 1}</span><p>${escapeHtml(finding)}</p></div>
          <div><span>Meaning</span><p>${index === 0 ? "Your measurement range anchors the recommendation." : "This context changes the support and shape choice."}</p></div>
          <div><span>Full report</span><p>Unlock the evidence review and cart-ready setup plan.</p></div>
        </div>
      `).join("")}
    </div>
    <div class="hfa-metrics">
      ${(report?.visibleMetrics ?? []).map((metric) => `
        <div class="hfa-metric"><span>${escapeHtml(metric.label)}</span><strong>${escapeHtml(metric.value)}</strong></div>
      `).join("")}
    </div>
    <form data-hfa-lead class="hfa-card">
      <p class="hfa-label">${escapeHtml(report?.unlockPrompt ?? "Unlock your report.")}</p>
      <div style="height:10px"></div>
      <div class="hfa-grid">
        <div class="hfa-field">
          <label for="hfa-email">Email</label>
          <input id="hfa-email" name="email" type="email" required placeholder="you@example.com" value="${escapeHtml(state.email)}" />
        </div>
        <div class="hfa-field">
          <label>&nbsp;</label>
          <button class="hfa-btn hfa-btn-primary" type="submit" ${state.loading ? "disabled" : ""}>${iconHtml("Mail")} Unlock report</button>
        </div>
      </div>
      <label class="hfa-status" style="margin-top:12px">
        <input type="checkbox" name="marketingConsent" ${state.marketingConsent ? "checked" : ""} />
        <span>Email me the report, fit tips, and relevant offers.</span>
      </label>
      <p class="hfa-fineprint">You can use the advisor without opting into marketing. The report is ergonomic desk-comfort guidance only.</p>
    </form>
  `, "", state);
}

function list(items: string[]): string {
  return `<ul>${items.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul>`;
}

type ProductMatch = FullReport["ownedSkuMatches"][number];

function bandLabel(key: keyof FullReport["comfortScore"]["bands"]): string {
  const labels: Record<keyof FullReport["comfortScore"]["bands"], string> = {
    fitMatch: "Fit match",
    wristLoad: "Wrist load",
    fingerTension: "Finger tension",
    deskPressure: "Desk pressure",
    breakHabit: "Break habit",
    skinSurface: "Skin surface",
  };
  return labels[key];
}

function bandStatus(value: number): string {
  if (value >= 82) return "Clear";
  if (value >= 70) return "Watch";
  return "Priority";
}

function bandCopy(key: keyof FullReport["comfortScore"]["bands"], value: number): string {
  const copies: Record<keyof FullReport["comfortScore"]["bands"], string> = {
    fitMatch: value >= 82 ? "Your measurements fit the strongest product range." : "Confirm the exact hand size before choosing a final shape.",
    wristLoad: value >= 82 ? "Your current answers do not point to major wrist-load pressure." : "Favor shapes that keep the wrist closer to neutral.",
    fingerTension: value >= 82 ? "Finger load looks manageable with a relaxed grip." : "Reduce pinch effort with a smoother surface or lighter movement path.",
    deskPressure: value >= 82 ? "Desk-edge pressure is not the main limiter." : "Use low, soft support without lifting the wrist too high.",
    breakHabit: value >= 82 ? "Your break rhythm supports long-session comfort." : "Short resets will matter as much as the product choice.",
    skinSurface: value >= 82 ? "Visible surface observations are low priority." : "Reduce friction and avoid hard pressure on sensitive areas.",
  };
  return copies[key];
}

function renderComfortFindings(report: FullReport): string {
  const keys: Array<keyof FullReport["comfortScore"]["bands"]> = ["wristLoad", "fingerTension", "deskPressure", "breakHabit"];
  return `
    <section class="hfa-report-section">
      <div class="hfa-section-head">
        <div>
          <p class="hfa-kicker">Comfort Findings</p>
          <h2>${escapeHtml(report.comfortScore.label)}</h2>
        </div>
        <span class="hfa-score-badge">${report.comfortScore.score}/100</span>
      </div>
      <p class="hfa-copy hfa-tight-copy">${escapeHtml(report.comfortScore.summary)}</p>
      <div class="hfa-finding-grid">
        ${keys.map((key) => {
          const value = report.comfortScore.bands[key];
          return `
            <article class="hfa-finding">
              <div class="hfa-finding-top">
                <span>${bandLabel(key)}</span>
                <strong>${bandStatus(value)}</strong>
              </div>
              <div class="hfa-bar" style="--value:${value}"><span></span></div>
              <p>${escapeHtml(bandCopy(key, value))}</p>
            </article>
          `;
        }).join("")}
      </div>
    </section>
  `;
}

function renderProductVisual(product: FullReport["ownedSkuMatches"][number]): string {
  if (!product.imageUrl) {
    return `<div class="hfa-product-visual" data-accent="${escapeHtml(product.accent)}" aria-hidden="true"></div>`;
  }
  return `
    <div class="hfa-product-visual has-image" data-accent="${escapeHtml(product.accent)}">
      <img src="${escapeHtml(product.imageUrl)}" alt="${escapeHtml(product.title)}" loading="lazy" />
    </div>
  `;
}

function matchRole(product: ProductMatch): string {
  if (product.score >= 82) return "Best match";
  if (product.score >= 74) return "Support pick";
  if (product.score >= 65) return "Backup option";
  return "Only if needed";
}

function matchTone(product: ProductMatch): string {
  if (product.score >= 82) return "strong";
  if (product.score >= 74) return "support";
  if (product.score >= 65) return "caution";
  return "low";
}

function visibleMatches(report: FullReport): ProductMatch[] {
  const strongEnough = report.ownedSkuMatches.filter((product) => product.score >= 65);
  if (!strongEnough.length) return report.ownedSkuMatches.slice(0, 1);
  const bestScore = strongEnough[0]?.score ?? 0;
  const primaryDevice = strongEnough.find((product) => {
    const type = product.productType.toLowerCase();
    const isPointerDevice = type.includes("trackball") || type.includes("combo") || (type.includes("mouse") && !type.includes("mouse pad"));
    return isPointerDevice && product.score >= bestScore - 12;
  });
  const ordered = primaryDevice
    ? [primaryDevice, ...strongEnough.filter((product) => product.productId !== primaryDevice.productId)]
    : strongEnough;
  return ordered.slice(0, 3);
}

function productBestFor(product: ProductMatch): string {
  return product.reasons[3] ?? product.reasons[1] ?? product.description;
}

function productTradeoff(product: ProductMatch): string {
  return product.cautions[0] ?? (product.score >= 82 ? "Strong fit, but confirm the feel after one full work session." : "Useful in this setup, but not the strongest geometry match.");
}

function productWatchOut(product: ProductMatch): string {
  return product.cautions[1] ?? "Not ideal if the first workday feel increases pressure or grip effort.";
}

function renderProductCard(product: ProductMatch, index: number): string {
  return `
    <article class="hfa-setup-card ${index === 0 ? "is-primary" : ""}">
      ${renderProductVisual(product)}
      <div class="hfa-setup-body">
        <div class="hfa-product-meta">
          <span class="hfa-match-label is-${matchTone(product)}">${matchRole(product)}</span>
          <span class="hfa-pill">$${product.priceUsd}</span>
          <span class="hfa-pill">${escapeHtml(displayText(product.productType))}</span>
        </div>
        <h3>${escapeHtml(product.title)}</h3>
        <p>${escapeHtml(product.description)}</p>
        <dl class="hfa-product-fields">
          <div><dt>Why it fits</dt><dd>${escapeHtml([product.reasons[0], product.reasons[1]].filter(Boolean).join(" ") || "It is the closest current match to your fit profile.")}</dd></div>
          <div><dt>Best if</dt><dd>${escapeHtml(productBestFor(product))}</dd></div>
          <div><dt>Tradeoff</dt><dd>${escapeHtml(productTradeoff(product))}</dd></div>
          <div><dt>Not ideal if</dt><dd>${escapeHtml(productWatchOut(product))}</dd></div>
        </dl>
        <div class="hfa-product-actions">
          <button class="hfa-btn hfa-btn-primary" data-hfa-cart="${escapeHtml(product.productId)}">${iconHtml("ShoppingCart")} Add to cart</button>
          ${product.productUrl ? `<a class="hfa-btn hfa-btn-secondary" href="${escapeHtml(product.productUrl)}" target="_blank" rel="noopener">${iconHtml("ExternalLink")} View details</a>` : ""}
        </div>
      </div>
    </article>
  `;
}

function renderProtocolCard(title: string, kicker: string, items: string[]): string {
  return `
    <article class="hfa-protocol-card">
      <p class="hfa-kicker">${escapeHtml(kicker)}</p>
      <h3>${escapeHtml(title)}</h3>
      ${list(items)}
    </article>
  `;
}

function measurementSummary(state: WidgetState): string {
  const metrics = state.analysis?.handMetrics;
  if (!metrics) return "Measurement data is attached to this report session.";
  return `${Math.round(metrics.handLengthMm)} mm hand length, ${Math.round(metrics.palmWidthMm)} mm palm width, ${metrics.confidence} confidence.`;
}

function answerLabel<K extends keyof Answers>(map: Record<string, string>, value: Answers[K]): string {
  return map[String(value)] ?? String(value).replaceAll("_", " ");
}

function deskSetupLabel(value: Answers["deskSetup"]): string {
  return answerLabel({
    desk_monitor: "Monitor desk",
    laptop_first: "Laptop-first",
    standing_desk: "Standing desk",
    compact_desk: "Compact desk",
    shared_workspace: "Shared workspace",
  }, value);
}

function surfaceLabel(value: Answers["currentSurface"]): string {
  return answerLabel({
    bare_desk: "Bare desk",
    small_pad: "Small pad",
    full_desk_mat: "Full desk mat",
    wrist_rest_pad: "Wrist-rest pad",
  }, value);
}

function priorityLabel(value: Answers["priority"]): string {
  return answerLabel({
    comfort: "Comfort",
    precision: "Precision",
    speed: "Speed",
    portability: "Portability",
    quiet: "Quiet",
  }, value);
}

function pointerSpeedLabel(value: Answers["pointerSpeed"]): string {
  return answerLabel({
    low_control: "Low control",
    balanced: "Balanced",
    fast: "Fast",
    not_sure: "Not sure",
  }, value);
}

function renderUserContext(state: WidgetState): string {
  const answers = state.answers;
  return `
    <div class="hfa-context-strip">
      <div><span>Desk</span><strong>${escapeHtml(deskSetupLabel(answers.deskSetup))}</strong></div>
      <div><span>Surface</span><strong>${escapeHtml(surfaceLabel(answers.currentSurface))}</strong></div>
      <div><span>Priority</span><strong>${escapeHtml(priorityLabel(answers.priority))}</strong></div>
      <div><span>Pointer feel</span><strong>${escapeHtml(pointerSpeedLabel(answers.pointerSpeed))}</strong></div>
    </div>
  `;
}

function renderEvidenceReview(report: FullReport): string {
  return `
    <section class="hfa-report-section">
      <div class="hfa-section-head">
        <div>
          <p class="hfa-kicker">Evidence Review</p>
          <h2>What the advisor used</h2>
        </div>
      </div>
      <div class="hfa-evidence-table">
        ${report.evidenceReview.slice(0, 5).map((item) => `
          <div class="hfa-evidence-row">
            <div><span>Evidence</span><p>${escapeHtml(item.evidence)}</p></div>
            <div><span>Meaning</span><p>${escapeHtml(item.meaning)}</p></div>
            <div><span>Recommended adjustment</span><p>${escapeHtml(item.recommendation)}</p></div>
          </div>
        `).join("")}
      </div>
    </section>
  `;
}

function renderPriorityPlan(report: FullReport): string {
  return `
    <section class="hfa-report-section">
      <div class="hfa-section-head">
        <div>
          <p class="hfa-kicker">Priority Plan</p>
          <h2>Fix first, then optimize</h2>
        </div>
      </div>
      <div class="hfa-priority-grid">
        <article class="hfa-priority-card">
          <h3>Fix first</h3>
          ${list(report.priorityPlan.fixFirst)}
        </article>
        <article class="hfa-priority-card">
          <h3>Optimize next</h3>
          ${list(report.priorityPlan.optimizeNext)}
        </article>
        <article class="hfa-priority-card">
          <h3>Avoid</h3>
          ${list(report.priorityPlan.avoid)}
        </article>
      </div>
    </section>
  `;
}

function renderAssessmentDimensions(report: FullReport): string {
  return `
    <section class="hfa-report-section">
      <div class="hfa-section-head">
        <div>
          <p class="hfa-kicker">Comfort Assessment</p>
          <h2>${escapeHtml(report.comfortAssessment.label)}</h2>
        </div>
        <span class="hfa-score-badge">${report.comfortScore.score}/100</span>
      </div>
      <p class="hfa-copy hfa-tight-copy">${escapeHtml(report.comfortAssessment.summary)}</p>
      <div class="hfa-dimension-grid">
        ${report.comfortAssessment.dimensions.slice(0, 5).map((item) => `
          <article class="hfa-dimension">
            <div class="hfa-dimension-head">
              <span>${escapeHtml(item.label)}</span>
              <strong class="hfa-status-chip is-${item.status}">${escapeHtml(item.status)}</strong>
            </div>
            <div class="hfa-bar" style="--value:${item.score}"><span></span></div>
            <p>${escapeHtml(item.meaning)}</p>
            <p><strong>Adjust:</strong> ${escapeHtml(item.recommendation)}</p>
          </article>
        `).join("")}
      </div>
    </section>
  `;
}

function setupMatches(report: FullReport): ProductMatch[] {
  const products = visibleMatches(report);
  const primary = report.recommendedSetup.primaryProductId
    ? products.find((product) => product.productId === report.recommendedSetup.primaryProductId)
    : products[0];
  if (!primary) return products;
  const support = report.recommendedSetup.supportProductIds
    .map((id) => products.find((product) => product.productId === id))
    .filter(Boolean) as ProductMatch[];
  const rest = products.filter((product) => product.productId !== primary.productId && !support.some((item) => item.productId === product.productId));
  return [primary, ...support, ...rest].slice(0, 3);
}

function renderRecommendedSetup(report: FullReport, state: WidgetState): string {
  const products = setupMatches(report);
  const primary = products[0];
  const secondary = products.slice(1);
  return `
    ${state.cart ? `
      <div class="hfa-success">
        <strong>Cart ready.</strong>
        <p>Your report stays here. Continue to checkout when you are ready, or keep comparing the setup notes.</p>
        <a class="hfa-btn hfa-btn-primary" data-hfa-checkout href="${escapeHtml(state.cart.checkoutUrl)}" target="_blank" rel="noopener">${iconHtml("ExternalLink")} Continue to checkout</a>
      </div>
    ` : ""}
    <section class="hfa-report-section">
      <div class="hfa-section-head">
        <div>
          <p class="hfa-kicker">Recommended Setup</p>
          <h2>${escapeHtml(report.recommendedSetup.headline)}</h2>
        </div>
      </div>
      <div class="hfa-product-intro">
        <strong>Why this setup comes after the assessment</strong>
        <p>${escapeHtml(report.recommendedSetup.rationale)}</p>
      </div>
      <div class="hfa-setup-list">
        ${primary ? renderProductCard(primary, 0) : ""}
      </div>
      ${secondary.length ? `
        <details class="hfa-alt-details" open>
          <summary>Support picks and backups</summary>
          <div style="height:12px"></div>
          <div class="hfa-setup-list">
            ${secondary.map((product, index) => renderProductCard(product, index + 1)).join("")}
          </div>
        </details>
      ` : ""}
      ${report.bundleRecommendations.length ? `
        <div class="hfa-bundle-strip">
          ${report.bundleRecommendations.slice(0, 2).map((bundle) => `
            <article>
              <span>${escapeHtml(bundle.focus.replaceAll("_", " "))}</span>
              <strong>${escapeHtml(bundle.title)}</strong>
              <p>${escapeHtml(bundle.reason)}</p>
            </article>
          `).join("")}
        </div>
      ` : ""}
    </section>
  `;
}

function renderRecoveryProtocol(report: FullReport): string {
  return `
    <section class="hfa-report-section">
      <div class="hfa-section-head">
        <div>
          <p class="hfa-kicker">Desk Recovery Protocol</p>
          <h2>Non-brand ways to reduce desk fatigue</h2>
        </div>
      </div>
      <div class="hfa-protocol-grid">
        ${renderProtocolCard("Workday reset", "During work", report.recoveryProtocol.workdayReset)}
        ${renderProtocolCard("Surface setup", "Desk support", report.recoveryProtocol.surfaceSetup)}
        ${renderProtocolCard("Break rhythm", "Behavior", report.recoveryProtocol.breakRhythm)}
      </div>
      <div style="height:12px"></div>
      <div class="hfa-protocol-grid">
        ${report.shoppingRules.slice(0, 3).map((item) => `
          <article class="hfa-protocol-card">
            <p class="hfa-kicker">Non-brand buying rule</p>
            <h3>${escapeHtml(item.rule)}</h3>
            <p>${escapeHtml(item.why)}</p>
          </article>
        `).join("")}
      </div>
    </section>
  `;
}

function renderReport(state: WidgetState): string {
  const report = state.report;
  if (!report) return renderPartial(state);
  const insights = report.advisorInsights;
  return shell(`
    <section class="hfa-report-hero">
      <div class="hfa-assessment-top">
        <div>
          <p class="hfa-kicker">Your Desk Comfort Assessment</p>
          <h1 class="hfa-title">${escapeHtml(report.executiveSummary.profileName || insights.profileName || report.fitProfile.title)}</h1>
          <p class="hfa-copy">${escapeHtml(report.executiveSummary.verdict || insights.oneLineSummary || report.fitProfile.summary)}</p>
          <div class="hfa-profile-tags">
            <span>${escapeHtml(report.executiveSummary.confidenceLabel || `${report.confidence} confidence`)}</span>
            <span>${escapeHtml(measurementSummary(state))}</span>
            <span>${escapeHtml(report.comfortAssessment.label || report.comfortScore.label)}</span>
          </div>
        </div>
        <aside class="hfa-verdict-panel">
          <span>First priority</span>
          <strong>${escapeHtml(report.executiveSummary.primaryAction)}</strong>
        </aside>
      </div>
      ${renderUserContext(state)}
    </section>
    <section class="hfa-report-section">
      <div class="hfa-section-head">
        <div>
          <p class="hfa-kicker">Assessment Summary</p>
          <h2>What this report is saying</h2>
        </div>
      </div>
      <div class="hfa-insight-grid">
        ${report.executiveSummary.summaryBullets.slice(0, 4).map((finding, index) => `
          <article class="hfa-insight">
            <span>${String(index + 1).padStart(2, "0")}</span>
            <p>${escapeHtml(finding)}</p>
          </article>
        `).join("")}
      </div>
    </section>
    ${renderEvidenceReview(report)}
    ${renderAssessmentDimensions(report)}
    ${renderPriorityPlan(report)}
    ${renderRecommendedSetup(report, state)}
    ${renderRecoveryProtocol(report)}
    <section class="hfa-report-footer">
      <article>
        <h3>Fit cautions</h3>
        ${list(report.avoidList.slice(0, 3))}
      </article>
      <article>
        <h3>When to pause</h3>
        ${list(report.redFlags.slice(0, 3))}
      </article>
      <article>
        <h3>Method note</h3>
        <p>${escapeHtml(report.methodNote || report.disclaimer)}</p>
        <p>${escapeHtml(measurementSummary(state))}</p>
      </article>
    </section>
  `, "", state);
}

function render(root: HTMLElement, state: WidgetState): void {
  root.innerHTML = state.step === "workSetup"
    ? renderWorkSetup(state)
    : state.step === "currentDevice"
      ? renderCurrentDevice(state)
      : state.step === "handGrip"
        ? renderHandGrip(state)
        : state.step === "photo"
          ? renderPhoto(state)
          : state.step === "calibrate"
            ? renderCalibrate(state)
            : state.step === "comfortSignals"
              ? renderComfortSignals(state)
              : state.step === "partial"
                ? renderPartial(state)
                : renderReport(state);
  bindEvents(root, state);
  if (state.step === "calibrate") drawCalibrationCanvas(state, root);
}

function readAnswers(form: HTMLFormElement, previous: Answers): Answers {
  const formData = new FormData(form);
  const hasField = (name: string) => Boolean(form.querySelector(`[name="${name}"]`));
  const discomfort = hasField("discomfort") ? formData.getAll("discomfort") as Answers["discomfort"] : previous.discomfort;
  const normalizedDiscomfort: Answers["discomfort"] = discomfort.includes("none") && discomfort.length > 1
    ? discomfort.filter((item) => item !== "none")
    : discomfort.length ? discomfort : ["none"];
  const discomfortAreas = hasField("discomfortAreas") ? formData.getAll("discomfortAreas") as Answers["discomfortAreas"] : previous.discomfortAreas;
  const normalizedAreas: Answers["discomfortAreas"] = discomfortAreas.includes("none") && discomfortAreas.length > 1
    ? discomfortAreas.filter((item) => item !== "none")
    : discomfortAreas.length ? discomfortAreas : ["none"];
  const currentDevicePainPoints = hasField("currentDevicePainPoints")
    ? formData.getAll("currentDevicePainPoints") as NonNullable<Answers["currentDevicePainPoints"]>
    : previous.currentDevicePainPoints ?? ["none"];
  const normalizedPainPoints: NonNullable<Answers["currentDevicePainPoints"]> = currentDevicePainPoints.includes("none") && currentDevicePainPoints.length > 1
    ? currentDevicePainPoints.filter((item) => item !== "none")
    : currentDevicePainPoints.length ? currentDevicePainPoints : ["none"];
  const budgetMin = Number(formData.get("budgetMinUsd") || previous.budgetMinUsd);
  const budgetMax = Number(formData.get("budgetMaxUsd") || previous.budgetMaxUsd);
  const dailyHours = Number(formData.get("dailyHours") || previous.dailyHours);
  const manualLength = Number(formData.get("manualHandLengthMm") || "");
  const manualWidth = Number(formData.get("manualPalmWidthMm") || "");
  return {
    mainGoal: (formData.get("mainGoal") || previous.mainGoal) as Answers["mainGoal"],
    currentDeviceType: (formData.get("currentDeviceType") || previous.currentDeviceType) as Answers["currentDeviceType"],
    currentDevicePainPoints: normalizedPainPoints,
    deskSpace: (formData.get("deskSpace") || previous.deskSpace) as Answers["deskSpace"],
    forearmSupport: (formData.get("forearmSupport") || previous.forearmSupport) as Answers["forearmSupport"],
    purchaseIntent: (formData.get("purchaseIntent") || previous.purchaseIntent) as Answers["purchaseIntent"],
    measurementMode: previous.measurementMode,
    dominantHand: (formData.get("dominantHand") || previous.dominantHand) as Answers["dominantHand"],
    gripStyle: (formData.get("gripStyle") || previous.gripStyle) as Answers["gripStyle"],
    usage: (formData.get("usage") || previous.usage) as Answers["usage"],
    discomfort: normalizedDiscomfort,
    discomfortAreas: normalizedAreas,
    dailyHours: Number.isFinite(dailyHours) ? Math.min(16, Math.max(1, Math.round(dailyHours))) : previous.dailyHours,
    breakFrequency: (formData.get("breakFrequency") || previous.breakFrequency) as Answers["breakFrequency"],
    deskSetup: (formData.get("deskSetup") || previous.deskSetup) as Answers["deskSetup"],
    keyboardStyle: (formData.get("keyboardStyle") || previous.keyboardStyle) as Answers["keyboardStyle"],
    currentSurface: (formData.get("currentSurface") || previous.currentSurface) as Answers["currentSurface"],
    pointerSpeed: (formData.get("pointerSpeed") || previous.pointerSpeed) as Answers["pointerSpeed"],
    priority: (formData.get("priority") || previous.priority) as Answers["priority"],
    skinSurface: (formData.get("skinSurface") || previous.skinSurface) as Answers["skinSurface"],
    budgetMinUsd: Math.min(budgetMin, budgetMax),
    budgetMaxUsd: Math.max(budgetMin, budgetMax),
    currentMouse: String(formData.get("currentMouse") || previous.currentMouse || ""),
    manualHandLengthMm: Number.isFinite(manualLength) && manualLength > 0 ? manualLength : previous.manualHandLengthMm,
    manualPalmWidthMm: Number.isFinite(manualWidth) && manualWidth > 0 ? manualWidth : previous.manualPalmWidthMm,
  };
}

function syncChoiceCards(form: HTMLFormElement): void {
  form.querySelectorAll<HTMLInputElement>('input[name="gripStyle"]').forEach((input) => {
    input.closest(".hfa-choice")?.classList.toggle("is-selected", input.checked);
    input.closest(".hfa-choice")?.setAttribute("aria-checked", input.checked ? "true" : "false");
  });
  form.querySelectorAll<HTMLInputElement>('input[name="discomfort"]').forEach((input) => {
    input.closest(".hfa-choice")?.classList.toggle("is-selected", input.checked);
    input.closest(".hfa-choice")?.setAttribute("aria-checked", input.checked ? "true" : "false");
  });
  form.querySelectorAll<HTMLInputElement>('input[name="discomfortAreas"]').forEach((input) => {
    input.closest(".hfa-choice")?.classList.toggle("is-selected", input.checked);
    input.closest(".hfa-choice")?.setAttribute("aria-checked", input.checked ? "true" : "false");
  });
  form.querySelectorAll<HTMLInputElement>('input[name="currentDevicePainPoints"]').forEach((input) => {
    input.closest(".hfa-choice")?.classList.toggle("is-selected", input.checked);
    input.closest(".hfa-choice")?.setAttribute("aria-checked", input.checked ? "true" : "false");
  });
  ["mainGoal", "deskSetup", "deskSpace", "forearmSupport", "keyboardStyle", "currentDeviceType", "currentSurface", "pointerSpeed", "priority"].forEach((name) => {
    form.querySelectorAll<HTMLInputElement>(`input[name="${name}"]`).forEach((input) => {
      input.closest(".hfa-choice")?.classList.toggle("is-selected", input.checked);
      input.closest(".hfa-choice")?.setAttribute("aria-checked", input.checked ? "true" : "false");
    });
  });
}

function normalizeDiscomfortChoices(changedInput: HTMLInputElement): void {
  if (!["discomfort", "discomfortAreas", "currentDevicePainPoints"].includes(changedInput.name) || !changedInput.form) return;
  const inputs = Array.from(changedInput.form.querySelectorAll<HTMLInputElement>(`input[name="${changedInput.name}"]`));
  const noneInput = inputs.find((input) => input.value === "none");
  if (changedInput.value === "none" && changedInput.checked) {
    inputs.filter((input) => input.value !== "none").forEach((input) => {
      input.checked = false;
    });
  } else if (changedInput.checked) {
    if (noneInput) noneInput.checked = false;
  }
  if (!inputs.some((input) => input.checked) && noneInput) {
    noneInput.checked = true;
  }
}

function toggleChoiceCard(choice: HTMLElement): void {
  const input = choice.querySelector<HTMLInputElement>("input");
  const form = input?.form;
  if (!input || !form || input.disabled) return;

  if (input.type === "radio") {
    Array.from(form.querySelectorAll<HTMLInputElement>("input")).forEach((option) => {
      if (option.name === input.name) option.checked = option === input;
    });
  } else if (input.type === "checkbox") {
    input.checked = !input.checked;
  } else {
    return;
  }

  input.dispatchEvent(new Event("change", { bubbles: true }));
  syncChoiceCards(form);
}

function canvasPoint(event: MouseEvent, canvas: HTMLCanvasElement): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  return {
    x: (event.clientX - rect.left) * (canvas.width / rect.width),
    y: (event.clientY - rect.top) * (canvas.height / rect.height),
  };
}

function setCalibrationFromPoints(state: WidgetState): void {
  if (state.calibrationPoints.length !== 2) return;
  const [a, b] = state.calibrationPoints;
  const distance = Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
  state.calibration = {
    method: "credit_card_click",
    pxPerMm: distance / CARD_MM,
    confidence: distance > 80 ? "high" : "medium",
  };
}

function bindEvents(root: HTMLElement, state: WidgetState): void {
  if (!root.dataset.hfaChoiceDelegated) {
    root.dataset.hfaChoiceDelegated = "true";
    root.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const choice = target.closest<HTMLElement>("[data-hfa-choice]");
      if (!choice || !root.contains(choice)) return;
      event.preventDefault();
      event.stopPropagation();
      toggleChoiceCard(choice);
    }, true);
    root.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const choice = target.closest<HTMLElement>("[data-hfa-choice]");
      if (!choice || !root.contains(choice)) return;
      event.preventDefault();
      toggleChoiceCard(choice);
    });
  }

  root.querySelector<HTMLInputElement>("[data-hfa-photo]")?.addEventListener("change", async (event) => {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const form = input.closest("form");
    if (form instanceof HTMLFormElement) {
      state.answers = readAnswers(form, state.answers);
    }
    state.loading = true;
    state.loadingMessage = "Reading the photo and detecting hand landmarks...";
    state.error = undefined;
    render(root, state);
    try {
      await createSession(state);
      const result = await analyzePhoto(file);
      state.imageElement = result.image;
      state.imageUrl = result.image.src;
      state.landmarks = result.landmarks;
      state.imageQuality = result.quality;
      state.calibrationPoints = [];
      state.calibration = { method: "none", confidence: "low" };
      state.answers.measurementMode = "photo_estimate";
      state.step = "calibrate";
      if (result.quality.handDetected) await track(state, "photo_quality_passed", { width: result.quality.width, height: result.quality.height });
    } catch (error) {
      state.error = error instanceof Error ? error.message : "Photo analysis failed.";
    } finally {
      state.loading = false;
      state.loadingMessage = undefined;
      render(root, state);
    }
  });

  root.querySelector<HTMLCanvasElement>("[data-hfa-canvas]")?.addEventListener("click", (event) => {
    const canvas = event.currentTarget as HTMLCanvasElement;
    if (state.calibrationPoints.length >= 2) state.calibrationPoints = [];
    state.calibrationPoints.push(canvasPoint(event, canvas));
    setCalibrationFromPoints(state);
    render(root, state);
  });

  root.querySelector<HTMLButtonElement>("[data-hfa-reset-calibration]")?.addEventListener("click", () => {
    state.calibrationPoints = [];
    state.calibration = { method: "none", confidence: "low" };
    render(root, state);
  });

  root.querySelector<HTMLButtonElement>("[data-hfa-skip-calibration]")?.addEventListener("click", () => {
    state.calibration = { method: "manual", confidence: "medium" };
    state.answers.measurementMode = "manual";
    state.step = "comfortSignals";
    render(root, state);
  });

  root.querySelector<HTMLButtonElement>("[data-hfa-upload-again]")?.addEventListener("click", () => {
    state.imageUrl = undefined;
    state.imageElement = undefined;
    state.landmarks = undefined;
    state.imageQuality = undefined;
    state.calibrationPoints = [];
    state.calibration = { method: "none", confidence: "low" };
    state.answers.measurementMode = "estimate";
    state.step = "handGrip";
    state.error = undefined;
    render(root, state);
  });

  root.querySelector<HTMLButtonElement>("[data-hfa-calibration-next]")?.addEventListener("click", () => {
    setCalibrationFromPoints(state);
    state.answers.measurementMode = state.calibration.method === "credit_card_click" ? "photo_card" : "photo_estimate";
    state.step = "comfortSignals";
    render(root, state);
  });

  root.querySelectorAll<HTMLFormElement>("[data-hfa-step-form]").forEach((form) => {
    form.addEventListener("change", (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.form) return;
      normalizeDiscomfortChoices(input);
      syncChoiceCards(input.form);
    });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const currentForm = event.currentTarget as HTMLFormElement;
      state.answers = readAnswers(currentForm, state.answers);
      state.step = (currentForm.dataset.hfaNextStep as Step | undefined) ?? "comfortSignals";
      state.error = undefined;
      render(root, state);
    });
  });

  root.querySelector<HTMLButtonElement>("[data-hfa-continue-estimate]")?.addEventListener("click", (event) => {
    const form = (event.currentTarget as HTMLElement).closest("form");
    if (form instanceof HTMLFormElement) {
      state.answers = readAnswers(form, state.answers);
    }
    state.calibration = { method: "none", confidence: "low" };
    state.answers.measurementMode = "estimate";
    state.step = "comfortSignals";
    state.error = undefined;
    render(root, state);
  });

  root.querySelectorAll<HTMLButtonElement>("[data-hfa-back]").forEach((button) => {
    button.addEventListener("click", () => {
      const form = button.closest("form");
      if (form instanceof HTMLFormElement) {
        state.answers = readAnswers(form, state.answers);
      }
      const target = button.dataset.hfaBack as Step | undefined;
      if (target) state.step = target;
      state.error = undefined;
      render(root, state);
    });
  });

  const questionsForm = root.querySelector<HTMLFormElement>("[data-hfa-questions]");
  questionsForm?.addEventListener("change", (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.form) return;
    normalizeDiscomfortChoices(input);
    syncChoiceCards(input.form);
  });

  questionsForm?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    state.answers = readAnswers(form, state.answers);
    if (state.answers.manualHandLengthMm && state.answers.manualPalmWidthMm && state.calibration.method === "none") {
      state.calibration = { method: "manual", confidence: "medium" };
      state.answers.measurementMode = "manual";
    }
    state.loading = true;
    state.loadingMessage = "Building your preview and starting the AI report in the background...";
    state.error = undefined;
    render(root, state);
    try {
      await createSession(state);
      state.analysis = await apiFetch<AnalyzeResponse>(state, "/api/fit/analyze", {
        sessionId: state.sessionId,
        landmarks: state.landmarks,
        imageQuality: state.imageQuality ?? {
          width: 1200,
          height: 1200,
          blurScore: 0.45,
          brightness: 0.5,
          handDetected: false,
          landmarksCount: 0,
        },
        calibration: state.calibration,
        answers: state.answers,
      });
      state.step = "partial";
      await track(state, "partial_result_viewed", { confidence: state.analysis.handMetrics.confidence });
      await track(state, "summary_viewed", { confidence: state.analysis.handMetrics.confidence });
    } catch (error) {
      state.error = error instanceof Error ? error.message : "Could not create the fit preview.";
    } finally {
      state.loading = false;
      state.loadingMessage = undefined;
      render(root, state);
    }
  });

  root.querySelector<HTMLFormElement>("[data-hfa-lead]")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    state.email = String(formData.get("email") || "");
    state.marketingConsent = formData.get("marketingConsent") === "on" && getConsent(true).marketing;
    state.loading = true;
    state.loadingMessage = "Unlocking your report. The AI summary may take a few seconds...";
    state.error = undefined;
    render(root, state);
    try {
      const lead = await apiFetch<{ reportReady: boolean }>(state, "/api/fit/lead", {
        sessionId: state.sessionId,
        email: state.email,
        marketingConsent: state.marketingConsent,
        privacyVersion: "2026-05-14",
      });
      state.report = lead.reportReady
        ? await fetchReportIfReady(state)
        : await waitForReport(state, () => {
          state.loadingMessage = "The fit report is being finalized with product images and comfort tips...";
          render(root, state);
        });
      if (!state.report) {
        state.report = await waitForReport(state, () => {
          state.loadingMessage = "Almost there. Preparing the final recommendation cards...";
          render(root, state);
        });
      }
      state.step = "report";
      await track(state, "report_unlocked", {}, state.marketingConsent);
    } catch (error) {
      state.error = error instanceof Error ? error.message : "Could not unlock the report.";
    } finally {
      state.loading = false;
      state.loadingMessage = undefined;
      render(root, state);
    }
  });

  root.querySelectorAll<HTMLButtonElement>("[data-hfa-cart]").forEach((button) => {
    button.addEventListener("click", async () => {
      const productId = button.dataset.hfaCart;
      if (!productId) return;
      state.loading = true;
      state.loadingMessage = "Creating your recommended Shopify cart...";
      state.error = undefined;
      button.disabled = true;
      try {
        await track(state, "recommendation_clicked", { productId }, state.marketingConsent);
        const cart = await apiFetch<{ checkoutUrl: string }>(state, "/api/fit/cart", {
          sessionId: state.sessionId,
          productId,
          quantity: 1,
        });
        state.cart = { productId, checkoutUrl: cart.checkoutUrl };
        state.loading = false;
        state.loadingMessage = undefined;
        render(root, state);
      } catch (error) {
        state.error = error instanceof Error ? error.message : "Could not create checkout.";
        state.loading = false;
        state.loadingMessage = undefined;
        render(root, state);
      }
    });
  });

  root.querySelector<HTMLAnchorElement>("[data-hfa-checkout]")?.addEventListener("click", () => {
    void track(state, "checkout_redirected", { productId: state.cart?.productId }, state.marketingConsent);
  });
}

function mount(target: HTMLElement, options?: InitOptions): void {
  injectStyles();
  const state: WidgetState = {
    apiBase: apiBaseFromTarget(target, options),
    step: "workSetup",
    loading: false,
    calibration: { method: "none", confidence: "low" },
    calibrationPoints: [],
    answers: { ...defaultAnswers },
    email: "",
    marketingConsent: false,
  };
  render(target, state);
  createSession(state).catch(() => {
    state.error = "The fit service is not reachable yet.";
    render(target, state);
  });
}

function init(options?: InitOptions): void {
  const target = typeof options?.target === "string"
    ? document.querySelector<HTMLElement>(options.target)
    : options?.target ?? document.querySelector<HTMLElement>("[data-ai-hand-fit-advisor]");
  if (!target) return;
  mount(target, options);
}

window.AIHandFitAdvisor = { init };

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => init());
} else {
  init();
}
